// Web CRM (Business plane) — AI Agent suite (Learning Center, Knowledge Base, AI Analytics)
// static-UI translations. Split out of translations.js only for file size; merged into the same
// flat per-language lookup at load time (see translations.js). Same rules: STATIC UI CHROME ONLY,
// English default + fallback, never translate dynamic/user/backend content (knowledge-base entries,
// FAQ/answer/question text, generated content, tenant names, counts, dates, API status enums used in
// logic — only their displayed labels). Interpolated {count}/{date}/{time} values stay dynamic.
//
// Scope: components/Business/AIAgent/{LearningCenter,KnowledgeBase,AIAnalytics}.jsx. All keys
// prefixed `ak.*`. Common buttons/labels (Save, Cancel, Close, Refresh, Error, Edit, Delete, etc.)
// intentionally reuse the shared common.* set and are NOT redefined here.
//
// Every language below carries the identical key set; missing keys would simply fall back to English.

const aiKnowledgeTranslations = {
  en: {
    // ---- shared toast / state ----
    'ak.notAvailableYet': "Not available yet",
    'ak.missingInformation': "Missing Information",
    'ak.exportComplete': "Export Complete",
    // ---- category labels (fixed enum; value stays English) ----
    'ak.cat.general': "General",
    'ak.cat.billing': "Billing",
    'ak.cat.technical': "Technical",
    'ak.cat.account': "Account",
    'ak.cat.product': "Product",
    'ak.cat.shipping': "Shipping",
    // ---- LearningCenter.jsx ----
    'ak.trainingUnavailableDesc': "AI model training will be available in an upcoming release.",
    'ak.fillRequiredFields': "Please fill in all required fields",
    'ak.saveScenariosUnavailableDesc': "Saving training scenarios will be available in an upcoming release.",
    'ak.missingFeedback': "Missing Feedback",
    'ak.provideFeedbackContent': "Please provide feedback content",
    'ak.submitFeedbackUnavailableDesc': "Submitting AI feedback will be available in an upcoming release.",
    'ak.loadingLearningCenter': "Loading learning center...",
    'ak.learningCenterTitle': "AI Learning Center",
    'ak.learningCenterSubtitle': "Train, improve, and optimize AI performance",
    'ak.trainingActive': "Training Active",
    'ak.ready': "Ready",
    'ak.startTraining': "Start Training",
    'ak.pauseTraining': "Pause Training",
    'ak.modelAccuracy': "Model Accuracy",
    'ak.learningRate': "Learning Rate",
    'ak.trainingProgress': "Training Progress",
    'ak.lastTraining': "Last Training",
    'ak.tab.customerFeedback': "Customer Feedback",
    'ak.tab.trainingScenarios': "Training Scenarios",
    'ak.tab.learningOpportunities': "Learning Opportunities",
    'ak.tab.modelPerformance': "Model Performance",
    'ak.recentCustomerFeedback': "Recent Customer Feedback",
    'ak.addFeedback': "Add Feedback",
    'ak.submitAiFeedback': "Submit AI Feedback",
    'ak.submitAiFeedbackDesc': "Help improve our AI by providing feedback on recent interactions",
    'ak.conversationIdOptional': "Conversation ID (optional)",
    'ak.enterConversationId': "Enter conversation ID",
    'ak.rating': "Rating",
    'ak.feedbackRequired': "Feedback *",
    'ak.feedbackPlaceholder': "Describe your experience with the AI response...",
    'ak.suggestionsForImprovement': "Suggestions for Improvement",
    'ak.suggestionsPlaceholder': "How could the AI response be improved?",
    'ak.submitFeedback': "Submit Feedback",
    'ak.noFeedbackYet': "No feedback yet",
    'ak.noFeedbackDesc': "Customer feedback on AI interactions will appear here in an upcoming release.",
    'ak.needsReview': "Needs Review",
    'ak.conversationLabel': "Conversation:",
    'ak.importScenarios': "Import Scenarios",
    'ak.importTrainingScenarios': "Import Training Scenarios",
    'ak.importScenariosDesc': "Upload a JSON or CSV file with training scenarios",
    'ak.importDropHint': "Drag and drop or select a file to import training scenarios.",
    'ak.addScenario': "Add Scenario",
    'ak.createTrainingScenario': "Create Training Scenario",
    'ak.createTrainingScenarioDesc': "Add a new training scenario to improve AI responses",
    'ak.scenarioTitleRequired': "Scenario Title *",
    'ak.enterScenarioTitle': "Enter scenario title",
    'ak.category': "Category",
    'ak.scenarioCatGeneral': "General Support",
    'ak.customerScenarioRequired': "Customer Scenario *",
    'ak.customerScenarioPlaceholder': "Describe the customer situation or question...",
    'ak.expectedAiResponseRequired': "Expected AI Response *",
    'ak.expectedAiResponsePlaceholder': "What should the AI respond in this scenario?",
    'ak.difficultyLevel': "Difficulty Level",
    'ak.difficulty.easy': "Easy",
    'ak.difficulty.medium': "Medium",
    'ak.difficulty.hard': "Hard",
    'ak.difficulty.expert': "Expert",
    'ak.tags': "Tags",
    'ak.tagsCommaPlaceholder': "Tags separated by commas",
    'ak.noScenariosYet': "No training scenarios yet",
    'ak.noScenariosDesc': "Saved training scenarios will appear here in an upcoming release.",
    'ak.scenarioLabel': "Scenario:",
    'ak.expectedResponseLabel': "Expected Response:",
    'ak.noOpportunitiesYet': "No learning opportunities yet",
    'ak.noOpportunitiesDesc': "Automated learning-opportunity detection will be available in an upcoming release.",
    'ak.prioritySuffix': "priority",
    'ak.recommendedAction': "Recommended Action:",
    'ak.expectedImpact': "Expected Impact:",
    'ak.implement': "Implement",
    'ak.schedule': "Schedule",
    'ak.modelPerformanceAnalytics': "Model Performance Analytics",
    'ak.aiModelPerformance': "AI Model Performance",
    'ak.learningProgress': "Learning Progress",
    'ak.trainingStatus': "Training Status",
    'ak.trainingStatusFallback': "AI auto-resolution runs on a hosted LLM; no local model-training metrics are recorded.",
    'ak.aiSolutionsPending': "AI solutions pending review",
    'ak.approvedIntoSolutionBank': "Approved into Solution Bank",
    'ak.trainingAchievements': "Training Achievements",
    'ak.noAchievementsYet': "No training achievements yet",
    'ak.noAchievementsDesc': "Milestones will appear here once model training is live.",
    'ak.exportReportUnavailableDesc': "Performance report export will be available in an upcoming release.",
    'ak.exportPerformanceReport': "Export Performance Report",
    // ---- KnowledgeBase.jsx ----
    'ak.failedLoadKb': "Failed to load knowledge base entries",
    'ak.fillTitleContent': "Please fill in the title and content",
    'ak.entryCreated': "Entry Created",
    'ak.entryCreatedDesc': "Knowledge base entry has been created successfully",
    'ak.failedCreateEntry': "Failed to create knowledge base entry",
    'ak.entryUpdated': "Entry Updated",
    'ak.entryUpdatedDesc': "Knowledge base entry has been updated successfully",
    'ak.failedUpdateEntry': "Failed to update knowledge base entry",
    'ak.entryDeleted': "Entry Deleted",
    'ak.entryDeletedDesc': "Knowledge base entry has been deleted successfully",
    'ak.failedDeleteEntry': "Failed to delete knowledge base entry",
    'ak.nothingToExport': "Nothing to export",
    'ak.nothingToExportDesc': "There are no knowledge base entries to export yet.",
    'ak.kbExportedDesc': "Knowledge base exported as JSON.",
    'ak.importUnavailableDesc': "Import will be available in an upcoming release.",
    'ak.loadingKb': "Loading knowledge base...",
    'ak.kbManagementTitle': "Knowledge Base Management",
    'ak.kbManagementSubtitle': "Manage AI knowledge entries and training content",
    'ak.entriesBadge': "{count} Entries",
    'ak.import': "Import",
    'ak.export': "Export",
    'ak.totalEntries': "Total Entries",
    'ak.avgSuccessRate': "Avg Success Rate",
    'ak.totalUsage': "Total Usage",
    'ak.categories': "Categories",
    'ak.tab.browseEntries': "Browse Entries",
    'ak.tab.createEntry': "Create Entry",
    'ak.tab.analytics': "Analytics",
    'ak.searchKbPlaceholder': "Search knowledge base...",
    'ak.allCategories': "All Categories",
    'ak.usedTimesUpdated': "Used {count} times · Last updated {date}",
    'ak.resolutionStepsLabel': "Resolution Steps:",
    'ak.noEntriesFound': "No entries found",
    'ak.noEntriesDesc': "Try adjusting your search or create a new knowledge base entry",
    'ak.createKbEntry': "Create Knowledge Base Entry",
    'ak.entryTitleRequired': "Entry Title *",
    'ak.enterEntryTitle': "Enter entry title",
    'ak.contentRequired': "Content *",
    'ak.enterKbContent': "Enter the knowledge base content",
    'ak.tagsCommaSeparated': "Tags (comma-separated)",
    'ak.tagsExamplePlaceholder': "password, reset, login, security",
    'ak.resolutionStepsOnePerLine': "Resolution Steps (one per line)",
    'ak.resolutionStepsPlaceholder': "Step 1: Go to login page\nStep 2: Click forgot password\nStep 3: Enter email address",
    'ak.saveEntry': "Save Entry",
    'ak.kbAnalytics': "Knowledge Base Analytics",
    'ak.performanceOverview': "Performance Overview",
    'ak.overallSuccessRate': "Overall Success Rate",
    'ak.knowledgeCoverage': "Knowledge Coverage",
    'ak.usageFrequency': "Usage Frequency",
    'ak.topPerformingEntries': "Top Performing Entries",
    'ak.viewDetailedAnalytics': "View Detailed Analytics",
    'ak.editKbEntry': "Edit Knowledge Base Entry",
    'ak.editKbEntryDesc': "Modify the knowledge base entry details",
    'ak.updateEntry': "Update Entry",
    'ak.areYouSure': "Are you sure?",
    'ak.deleteConfirmPrefix': "This action cannot be undone. This will permanently delete the knowledge base entry",
    'ak.deleteConfirmSuffix': "and remove all associated data.",
    'ak.deleteEntry': "Delete Entry",
    'ak.detailedKbAnalytics': "Detailed Knowledge Base Analytics",
    'ak.detailedKbAnalyticsDesc': "Comprehensive analysis of knowledge base performance and insights",
    'ak.categoryPerformanceAnalysis': "Category Performance Analysis",
    'ak.entriesLower': "{count} entries",
    'ak.successRate': "Success Rate",
    'ak.usage': "Usage",
    'ak.aiPerformanceInsights': "AI Performance Insights",
    'ak.highPerformingContent': "High Performing Content",
    'ak.entriesAbove90': "{count} entries have success rates above 90%",
    'ak.optimizationOpportunities': "Optimization Opportunities",
    'ak.entriesBenefitOptimization': "{count} entries could benefit from optimization",
    'ak.usageTrendsRecommendations': "Usage Trends & Recommendations",
    'ak.mostUsedEntry': "Most Used Entry:",
    'ak.uses': "uses",
    'ak.bestPerformingEntry': "Best Performing Entry:",
    'ak.success': "success",
    'ak.aiRecommendations': "AI Recommendations:",
    'ak.recExpandCategories': "Consider expanding high-performing categories with more entries",
    'ak.recReviewLowSuccess': "Review and optimize entries with success rates below 80%",
    'ak.recAddResolutionSteps': "Add more resolution steps to frequently used entries",
    'ak.recMonitorFeedback': "Monitor customer feedback for content improvements",
    // ---- AIAnalytics.jsx ----
    'ak.failedLoadAnalytics': "Failed to load AI analytics data",
    'ak.analyticsExportedDesc': "AI analytics data has been exported successfully",
    'ak.loadingAnalytics': "Loading AI analytics...",
    'ak.analyticsDashboardTitle': "AI Analytics Dashboard",
    'ak.analyticsDashboardSubtitle': "Comprehensive AI performance metrics and insights",
    'ak.range.24h': "Last 24h",
    'ak.range.7d': "Last 7 days",
    'ak.range.30d': "Last 30 days",
    'ak.range.90d': "Last 90 days",
    'ak.lastUpdated': "Last updated: {time}",
    'ak.totalConversations': "Total Conversations",
    'ak.aiHandled': "AI handled",
    'ak.autoResolutionRate': "Auto-Resolution Rate",
    'ak.currentPeriod': "current period",
    'ak.avgResponseTime': "Avg Response Time",
    'ak.customerSatisfaction': "Customer Satisfaction",
    'ak.tab.categoryAnalysis': "Category Analysis",
    'ak.tab.trendsPatterns': "Trends & Patterns",
    'ak.tab.aiInsights': "AI Insights",
    'ak.resolutionPerformance': "Resolution Performance",
    'ak.successfulResolutions': "Successful Resolutions",
    'ak.escalationRate': "Escalation Rate",
    'ak.aiConfidenceScore': "AI Confidence Score",
    'ak.hourlyPerformance': "Hourly Performance",
    'ak.conversationsCount': "{count} conversations",
    'ak.performanceByCategory': "Performance by Category",
    'ak.performanceTrends': "Performance Trends",
    'ak.trendComparisonsUnavailable': "Trend comparisons are not available yet",
    'ak.trendComparisonsDesc': "Period-over-period trends will be available in an upcoming release.",
    'ak.usagePatterns': "Usage Patterns",
    'ak.peakHours': "Peak Hours",
    'ak.mostCommonIssues': "Most Common Issues",
    'ak.resolutionTime': "Resolution Time",
    'ak.average': "Average",
    'ak.aiInsightsUnavailable': "AI performance insights are not available yet",
    'ak.aiInsightsDesc': "Automated insight analysis will be available in an upcoming release.",
    'ak.bestPracticeGuidance': "Best-Practice Guidance",
    'ak.bestPracticeNote': "General guidance for improving AI performance — not derived from your live data.",
    'ak.immediateActions': "Immediate Actions",
    'ak.immediate1': "Add more technical support knowledge base entries",
    'ak.immediate2': "Review escalated conversations for pattern analysis",
    'ak.immediate3': "Update training data for complex billing scenarios",
    'ak.strategicImprovements': "Strategic Improvements",
    'ak.strategic1': "Implement sentiment analysis for better routing",
    'ak.strategic2': "Add multilingual support for global customers",
    'ak.strategic3': "Develop proactive issue detection capabilities",
    'ak.teamDevelopment': "Team Development",
    'ak.team1': "Train agents on AI-escalated cases",
    'ak.team2': "Create feedback loops for continuous improvement",
    'ak.team3': "Develop AI-human collaboration workflows",
  },
  es: {
    'ak.notAvailableYet': "Aún no disponible",
    'ak.missingInformation': "Falta información",
    'ak.exportComplete': "Exportación completa",
    'ak.cat.general': "General",
    'ak.cat.billing': "Facturación",
    'ak.cat.technical': "Técnico",
    'ak.cat.account': "Cuenta",
    'ak.cat.product': "Producto",
    'ak.cat.shipping': "Envío",
    'ak.trainingUnavailableDesc': "El entrenamiento del modelo de IA estará disponible en una próxima versión.",
    'ak.fillRequiredFields': "Por favor complete todos los campos obligatorios",
    'ak.saveScenariosUnavailableDesc': "Guardar escenarios de entrenamiento estará disponible en una próxima versión.",
    'ak.missingFeedback': "Falta comentario",
    'ak.provideFeedbackContent': "Por favor proporcione el contenido del comentario",
    'ak.submitFeedbackUnavailableDesc': "El envío de comentarios de IA estará disponible en una próxima versión.",
    'ak.loadingLearningCenter': "Cargando centro de aprendizaje...",
    'ak.learningCenterTitle': "Centro de aprendizaje de IA",
    'ak.learningCenterSubtitle': "Entrena, mejora y optimiza el rendimiento de la IA",
    'ak.trainingActive': "Entrenamiento activo",
    'ak.ready': "Listo",
    'ak.startTraining': "Iniciar entrenamiento",
    'ak.pauseTraining': "Pausar entrenamiento",
    'ak.modelAccuracy': "Precisión del modelo",
    'ak.learningRate': "Tasa de aprendizaje",
    'ak.trainingProgress': "Progreso del entrenamiento",
    'ak.lastTraining': "Último entrenamiento",
    'ak.tab.customerFeedback': "Comentarios de clientes",
    'ak.tab.trainingScenarios': "Escenarios de entrenamiento",
    'ak.tab.learningOpportunities': "Oportunidades de aprendizaje",
    'ak.tab.modelPerformance': "Rendimiento del modelo",
    'ak.recentCustomerFeedback': "Comentarios recientes de clientes",
    'ak.addFeedback': "Agregar comentario",
    'ak.submitAiFeedback': "Enviar comentario de IA",
    'ak.submitAiFeedbackDesc': "Ayude a mejorar nuestra IA proporcionando comentarios sobre interacciones recientes",
    'ak.conversationIdOptional': "ID de conversación (opcional)",
    'ak.enterConversationId': "Ingrese el ID de conversación",
    'ak.rating': "Calificación",
    'ak.feedbackRequired': "Comentario *",
    'ak.feedbackPlaceholder': "Describa su experiencia con la respuesta de la IA...",
    'ak.suggestionsForImprovement': "Sugerencias de mejora",
    'ak.suggestionsPlaceholder': "¿Cómo podría mejorarse la respuesta de la IA?",
    'ak.submitFeedback': "Enviar comentario",
    'ak.noFeedbackYet': "Aún no hay comentarios",
    'ak.noFeedbackDesc': "Los comentarios de clientes sobre interacciones de IA aparecerán aquí en una próxima versión.",
    'ak.needsReview': "Requiere revisión",
    'ak.conversationLabel': "Conversación:",
    'ak.importScenarios': "Importar escenarios",
    'ak.importTrainingScenarios': "Importar escenarios de entrenamiento",
    'ak.importScenariosDesc': "Suba un archivo JSON o CSV con escenarios de entrenamiento",
    'ak.importDropHint': "Arrastre y suelte o seleccione un archivo para importar escenarios de entrenamiento.",
    'ak.addScenario': "Agregar escenario",
    'ak.createTrainingScenario': "Crear escenario de entrenamiento",
    'ak.createTrainingScenarioDesc': "Agregue un nuevo escenario de entrenamiento para mejorar las respuestas de la IA",
    'ak.scenarioTitleRequired': "Título del escenario *",
    'ak.enterScenarioTitle': "Ingrese el título del escenario",
    'ak.category': "Categoría",
    'ak.scenarioCatGeneral': "Soporte general",
    'ak.customerScenarioRequired': "Escenario del cliente *",
    'ak.customerScenarioPlaceholder': "Describa la situación o pregunta del cliente...",
    'ak.expectedAiResponseRequired': "Respuesta de IA esperada *",
    'ak.expectedAiResponsePlaceholder': "¿Qué debería responder la IA en este escenario?",
    'ak.difficultyLevel': "Nivel de dificultad",
    'ak.difficulty.easy': "Fácil",
    'ak.difficulty.medium': "Media",
    'ak.difficulty.hard': "Difícil",
    'ak.difficulty.expert': "Experto",
    'ak.tags': "Etiquetas",
    'ak.tagsCommaPlaceholder': "Etiquetas separadas por comas",
    'ak.noScenariosYet': "Aún no hay escenarios de entrenamiento",
    'ak.noScenariosDesc': "Los escenarios de entrenamiento guardados aparecerán aquí en una próxima versión.",
    'ak.scenarioLabel': "Escenario:",
    'ak.expectedResponseLabel': "Respuesta esperada:",
    'ak.noOpportunitiesYet': "Aún no hay oportunidades de aprendizaje",
    'ak.noOpportunitiesDesc': "La detección automática de oportunidades de aprendizaje estará disponible en una próxima versión.",
    'ak.prioritySuffix': "prioridad",
    'ak.recommendedAction': "Acción recomendada:",
    'ak.expectedImpact': "Impacto esperado:",
    'ak.implement': "Implementar",
    'ak.schedule': "Programar",
    'ak.modelPerformanceAnalytics': "Análisis del rendimiento del modelo",
    'ak.aiModelPerformance': "Rendimiento del modelo de IA",
    'ak.learningProgress': "Progreso del aprendizaje",
    'ak.trainingStatus': "Estado del entrenamiento",
    'ak.trainingStatusFallback': "La resolución automática por IA se ejecuta en un LLM alojado; no se registran métricas de entrenamiento de modelo local.",
    'ak.aiSolutionsPending': "Soluciones de IA pendientes de revisión",
    'ak.approvedIntoSolutionBank': "Aprobadas en el Banco de soluciones",
    'ak.trainingAchievements': "Logros de entrenamiento",
    'ak.noAchievementsYet': "Aún no hay logros de entrenamiento",
    'ak.noAchievementsDesc': "Los hitos aparecerán aquí una vez que el entrenamiento del modelo esté activo.",
    'ak.exportReportUnavailableDesc': "La exportación del informe de rendimiento estará disponible en una próxima versión.",
    'ak.exportPerformanceReport': "Exportar informe de rendimiento",
    'ak.failedLoadKb': "Error al cargar las entradas de la base de conocimiento",
    'ak.fillTitleContent': "Por favor complete el título y el contenido",
    'ak.entryCreated': "Entrada creada",
    'ak.entryCreatedDesc': "La entrada de la base de conocimiento se creó correctamente",
    'ak.failedCreateEntry': "Error al crear la entrada de la base de conocimiento",
    'ak.entryUpdated': "Entrada actualizada",
    'ak.entryUpdatedDesc': "La entrada de la base de conocimiento se actualizó correctamente",
    'ak.failedUpdateEntry': "Error al actualizar la entrada de la base de conocimiento",
    'ak.entryDeleted': "Entrada eliminada",
    'ak.entryDeletedDesc': "La entrada de la base de conocimiento se eliminó correctamente",
    'ak.failedDeleteEntry': "Error al eliminar la entrada de la base de conocimiento",
    'ak.nothingToExport': "Nada que exportar",
    'ak.nothingToExportDesc': "Aún no hay entradas de la base de conocimiento para exportar.",
    'ak.kbExportedDesc': "Base de conocimiento exportada como JSON.",
    'ak.importUnavailableDesc': "La importación estará disponible en una próxima versión.",
    'ak.loadingKb': "Cargando base de conocimiento...",
    'ak.kbManagementTitle': "Gestión de la base de conocimiento",
    'ak.kbManagementSubtitle': "Gestione las entradas de conocimiento de IA y el contenido de entrenamiento",
    'ak.entriesBadge': "{count} entradas",
    'ak.import': "Importar",
    'ak.export': "Exportar",
    'ak.totalEntries': "Total de entradas",
    'ak.avgSuccessRate': "Tasa de éxito promedio",
    'ak.totalUsage': "Uso total",
    'ak.categories': "Categorías",
    'ak.tab.browseEntries': "Explorar entradas",
    'ak.tab.createEntry': "Crear entrada",
    'ak.tab.analytics': "Analíticas",
    'ak.searchKbPlaceholder': "Buscar en la base de conocimiento...",
    'ak.allCategories': "Todas las categorías",
    'ak.usedTimesUpdated': "Usado {count} veces · Última actualización {date}",
    'ak.resolutionStepsLabel': "Pasos de resolución:",
    'ak.noEntriesFound': "No se encontraron entradas",
    'ak.noEntriesDesc': "Intente ajustar su búsqueda o cree una nueva entrada en la base de conocimiento",
    'ak.createKbEntry': "Crear entrada en la base de conocimiento",
    'ak.entryTitleRequired': "Título de la entrada *",
    'ak.enterEntryTitle': "Ingrese el título de la entrada",
    'ak.contentRequired': "Contenido *",
    'ak.enterKbContent': "Ingrese el contenido de la base de conocimiento",
    'ak.tagsCommaSeparated': "Etiquetas (separadas por comas)",
    'ak.tagsExamplePlaceholder': "contraseña, restablecer, inicio de sesión, seguridad",
    'ak.resolutionStepsOnePerLine': "Pasos de resolución (uno por línea)",
    'ak.resolutionStepsPlaceholder': "Paso 1: Ir a la página de inicio de sesión\nPaso 2: Hacer clic en olvidé mi contraseña\nPaso 3: Ingresar la dirección de correo electrónico",
    'ak.saveEntry': "Guardar entrada",
    'ak.kbAnalytics': "Analíticas de la base de conocimiento",
    'ak.performanceOverview': "Resumen de rendimiento",
    'ak.overallSuccessRate': "Tasa de éxito general",
    'ak.knowledgeCoverage': "Cobertura de conocimiento",
    'ak.usageFrequency': "Frecuencia de uso",
    'ak.topPerformingEntries': "Entradas de mejor rendimiento",
    'ak.viewDetailedAnalytics': "Ver analíticas detalladas",
    'ak.editKbEntry': "Editar entrada de la base de conocimiento",
    'ak.editKbEntryDesc': "Modifique los detalles de la entrada de la base de conocimiento",
    'ak.updateEntry': "Actualizar entrada",
    'ak.areYouSure': "¿Está seguro?",
    'ak.deleteConfirmPrefix': "Esta acción no se puede deshacer. Esto eliminará permanentemente la entrada de la base de conocimiento",
    'ak.deleteConfirmSuffix': "y eliminará todos los datos asociados.",
    'ak.deleteEntry': "Eliminar entrada",
    'ak.detailedKbAnalytics': "Analíticas detalladas de la base de conocimiento",
    'ak.detailedKbAnalyticsDesc': "Análisis integral del rendimiento y las perspectivas de la base de conocimiento",
    'ak.categoryPerformanceAnalysis': "Análisis de rendimiento por categoría",
    'ak.entriesLower': "{count} entradas",
    'ak.successRate': "Tasa de éxito",
    'ak.usage': "Uso",
    'ak.aiPerformanceInsights': "Perspectivas de rendimiento de IA",
    'ak.highPerformingContent': "Contenido de alto rendimiento",
    'ak.entriesAbove90': "{count} entradas tienen tasas de éxito superiores al 90%",
    'ak.optimizationOpportunities': "Oportunidades de optimización",
    'ak.entriesBenefitOptimization': "{count} entradas podrían beneficiarse de la optimización",
    'ak.usageTrendsRecommendations': "Tendencias de uso y recomendaciones",
    'ak.mostUsedEntry': "Entrada más usada:",
    'ak.uses': "usos",
    'ak.bestPerformingEntry': "Entrada de mejor rendimiento:",
    'ak.success': "éxito",
    'ak.aiRecommendations': "Recomendaciones de IA:",
    'ak.recExpandCategories': "Considere ampliar las categorías de alto rendimiento con más entradas",
    'ak.recReviewLowSuccess': "Revise y optimice las entradas con tasas de éxito inferiores al 80%",
    'ak.recAddResolutionSteps': "Agregue más pasos de resolución a las entradas de uso frecuente",
    'ak.recMonitorFeedback': "Supervise los comentarios de clientes para mejorar el contenido",
    'ak.failedLoadAnalytics': "Error al cargar los datos de analíticas de IA",
    'ak.analyticsExportedDesc': "Los datos de analíticas de IA se exportaron correctamente",
    'ak.loadingAnalytics': "Cargando analíticas de IA...",
    'ak.analyticsDashboardTitle': "Panel de analíticas de IA",
    'ak.analyticsDashboardSubtitle': "Métricas y perspectivas integrales de rendimiento de IA",
    'ak.range.24h': "Últimas 24h",
    'ak.range.7d': "Últimos 7 días",
    'ak.range.30d': "Últimos 30 días",
    'ak.range.90d': "Últimos 90 días",
    'ak.lastUpdated': "Última actualización: {time}",
    'ak.totalConversations': "Total de conversaciones",
    'ak.aiHandled': "Gestionadas por IA",
    'ak.autoResolutionRate': "Tasa de resolución automática",
    'ak.currentPeriod': "período actual",
    'ak.avgResponseTime': "Tiempo de respuesta promedio",
    'ak.customerSatisfaction': "Satisfacción del cliente",
    'ak.tab.categoryAnalysis': "Análisis por categoría",
    'ak.tab.trendsPatterns': "Tendencias y patrones",
    'ak.tab.aiInsights': "Perspectivas de IA",
    'ak.resolutionPerformance': "Rendimiento de resolución",
    'ak.successfulResolutions': "Resoluciones exitosas",
    'ak.escalationRate': "Tasa de escalación",
    'ak.aiConfidenceScore': "Puntuación de confianza de IA",
    'ak.hourlyPerformance': "Rendimiento por hora",
    'ak.conversationsCount': "{count} conversaciones",
    'ak.performanceByCategory': "Rendimiento por categoría",
    'ak.performanceTrends': "Tendencias de rendimiento",
    'ak.trendComparisonsUnavailable': "Las comparaciones de tendencias aún no están disponibles",
    'ak.trendComparisonsDesc': "Las tendencias período a período estarán disponibles en una próxima versión.",
    'ak.usagePatterns': "Patrones de uso",
    'ak.peakHours': "Horas pico",
    'ak.mostCommonIssues': "Problemas más comunes",
    'ak.resolutionTime': "Tiempo de resolución",
    'ak.average': "Promedio",
    'ak.aiInsightsUnavailable': "Las perspectivas de rendimiento de IA aún no están disponibles",
    'ak.aiInsightsDesc': "El análisis automático de perspectivas estará disponible en una próxima versión.",
    'ak.bestPracticeGuidance': "Guía de buenas prácticas",
    'ak.bestPracticeNote': "Guía general para mejorar el rendimiento de la IA, no derivada de sus datos en vivo.",
    'ak.immediateActions': "Acciones inmediatas",
    'ak.immediate1': "Agregar más entradas de soporte técnico a la base de conocimiento",
    'ak.immediate2': "Revisar conversaciones escaladas para análisis de patrones",
    'ak.immediate3': "Actualizar los datos de entrenamiento para escenarios de facturación complejos",
    'ak.strategicImprovements': "Mejoras estratégicas",
    'ak.strategic1': "Implementar análisis de sentimiento para un mejor enrutamiento",
    'ak.strategic2': "Agregar soporte multilingüe para clientes globales",
    'ak.strategic3': "Desarrollar capacidades de detección proactiva de problemas",
    'ak.teamDevelopment': "Desarrollo del equipo",
    'ak.team1': "Capacitar a los agentes en casos escalados por IA",
    'ak.team2': "Crear ciclos de retroalimentación para la mejora continua",
    'ak.team3': "Desarrollar flujos de trabajo de colaboración entre IA y humanos",
  },
  fr: {
    'ak.notAvailableYet': "Pas encore disponible",
    'ak.missingInformation': "Informations manquantes",
    'ak.exportComplete': "Exportation terminée",
    'ak.cat.general': "Général",
    'ak.cat.billing': "Facturation",
    'ak.cat.technical': "Technique",
    'ak.cat.account': "Compte",
    'ak.cat.product': "Produit",
    'ak.cat.shipping': "Expédition",
    'ak.trainingUnavailableDesc': "L'entraînement du modèle d'IA sera disponible dans une prochaine version.",
    'ak.fillRequiredFields': "Veuillez remplir tous les champs obligatoires",
    'ak.saveScenariosUnavailableDesc': "L'enregistrement des scénarios d'entraînement sera disponible dans une prochaine version.",
    'ak.missingFeedback': "Commentaire manquant",
    'ak.provideFeedbackContent': "Veuillez fournir le contenu du commentaire",
    'ak.submitFeedbackUnavailableDesc': "L'envoi de commentaires à l'IA sera disponible dans une prochaine version.",
    'ak.loadingLearningCenter': "Chargement du centre d'apprentissage...",
    'ak.learningCenterTitle': "Centre d'apprentissage de l'IA",
    'ak.learningCenterSubtitle': "Entraînez, améliorez et optimisez les performances de l'IA",
    'ak.trainingActive': "Entraînement actif",
    'ak.ready': "Prêt",
    'ak.startTraining': "Démarrer l'entraînement",
    'ak.pauseTraining': "Mettre en pause l'entraînement",
    'ak.modelAccuracy': "Précision du modèle",
    'ak.learningRate': "Taux d'apprentissage",
    'ak.trainingProgress': "Progression de l'entraînement",
    'ak.lastTraining': "Dernier entraînement",
    'ak.tab.customerFeedback': "Commentaires des clients",
    'ak.tab.trainingScenarios': "Scénarios d'entraînement",
    'ak.tab.learningOpportunities': "Opportunités d'apprentissage",
    'ak.tab.modelPerformance': "Performances du modèle",
    'ak.recentCustomerFeedback': "Commentaires récents des clients",
    'ak.addFeedback': "Ajouter un commentaire",
    'ak.submitAiFeedback': "Envoyer un commentaire à l'IA",
    'ak.submitAiFeedbackDesc': "Aidez à améliorer notre IA en fournissant des commentaires sur les interactions récentes",
    'ak.conversationIdOptional': "ID de conversation (facultatif)",
    'ak.enterConversationId': "Saisissez l'ID de conversation",
    'ak.rating': "Évaluation",
    'ak.feedbackRequired': "Commentaire *",
    'ak.feedbackPlaceholder': "Décrivez votre expérience avec la réponse de l'IA...",
    'ak.suggestionsForImprovement': "Suggestions d'amélioration",
    'ak.suggestionsPlaceholder': "Comment la réponse de l'IA pourrait-elle être améliorée ?",
    'ak.submitFeedback': "Envoyer le commentaire",
    'ak.noFeedbackYet': "Aucun commentaire pour le moment",
    'ak.noFeedbackDesc': "Les commentaires des clients sur les interactions de l'IA apparaîtront ici dans une prochaine version.",
    'ak.needsReview': "À revoir",
    'ak.conversationLabel': "Conversation :",
    'ak.importScenarios': "Importer des scénarios",
    'ak.importTrainingScenarios': "Importer des scénarios d'entraînement",
    'ak.importScenariosDesc': "Téléversez un fichier JSON ou CSV avec des scénarios d'entraînement",
    'ak.importDropHint': "Glissez-déposez ou sélectionnez un fichier pour importer des scénarios d'entraînement.",
    'ak.addScenario': "Ajouter un scénario",
    'ak.createTrainingScenario': "Créer un scénario d'entraînement",
    'ak.createTrainingScenarioDesc': "Ajoutez un nouveau scénario d'entraînement pour améliorer les réponses de l'IA",
    'ak.scenarioTitleRequired': "Titre du scénario *",
    'ak.enterScenarioTitle': "Saisissez le titre du scénario",
    'ak.category': "Catégorie",
    'ak.scenarioCatGeneral': "Support général",
    'ak.customerScenarioRequired': "Scénario client *",
    'ak.customerScenarioPlaceholder': "Décrivez la situation ou la question du client...",
    'ak.expectedAiResponseRequired': "Réponse attendue de l'IA *",
    'ak.expectedAiResponsePlaceholder': "Que devrait répondre l'IA dans ce scénario ?",
    'ak.difficultyLevel': "Niveau de difficulté",
    'ak.difficulty.easy': "Facile",
    'ak.difficulty.medium': "Moyen",
    'ak.difficulty.hard': "Difficile",
    'ak.difficulty.expert': "Expert",
    'ak.tags': "Étiquettes",
    'ak.tagsCommaPlaceholder': "Étiquettes séparées par des virgules",
    'ak.noScenariosYet': "Aucun scénario d'entraînement pour le moment",
    'ak.noScenariosDesc': "Les scénarios d'entraînement enregistrés apparaîtront ici dans une prochaine version.",
    'ak.scenarioLabel': "Scénario :",
    'ak.expectedResponseLabel': "Réponse attendue :",
    'ak.noOpportunitiesYet': "Aucune opportunité d'apprentissage pour le moment",
    'ak.noOpportunitiesDesc': "La détection automatique des opportunités d'apprentissage sera disponible dans une prochaine version.",
    'ak.prioritySuffix': "priorité",
    'ak.recommendedAction': "Action recommandée :",
    'ak.expectedImpact': "Impact attendu :",
    'ak.implement': "Mettre en œuvre",
    'ak.schedule': "Planifier",
    'ak.modelPerformanceAnalytics': "Analyse des performances du modèle",
    'ak.aiModelPerformance': "Performances du modèle d'IA",
    'ak.learningProgress': "Progression de l'apprentissage",
    'ak.trainingStatus': "État de l'entraînement",
    'ak.trainingStatusFallback': "La résolution automatique par l'IA s'exécute sur un LLM hébergé ; aucune métrique d'entraînement de modèle local n'est enregistrée.",
    'ak.aiSolutionsPending': "Solutions d'IA en attente de révision",
    'ak.approvedIntoSolutionBank': "Approuvées dans la Banque de solutions",
    'ak.trainingAchievements': "Réalisations d'entraînement",
    'ak.noAchievementsYet': "Aucune réalisation d'entraînement pour le moment",
    'ak.noAchievementsDesc': "Les jalons apparaîtront ici une fois l'entraînement du modèle actif.",
    'ak.exportReportUnavailableDesc': "L'exportation du rapport de performances sera disponible dans une prochaine version.",
    'ak.exportPerformanceReport': "Exporter le rapport de performances",
    'ak.failedLoadKb': "Échec du chargement des entrées de la base de connaissances",
    'ak.fillTitleContent': "Veuillez remplir le titre et le contenu",
    'ak.entryCreated': "Entrée créée",
    'ak.entryCreatedDesc': "L'entrée de la base de connaissances a été créée avec succès",
    'ak.failedCreateEntry': "Échec de la création de l'entrée de la base de connaissances",
    'ak.entryUpdated': "Entrée mise à jour",
    'ak.entryUpdatedDesc': "L'entrée de la base de connaissances a été mise à jour avec succès",
    'ak.failedUpdateEntry': "Échec de la mise à jour de l'entrée de la base de connaissances",
    'ak.entryDeleted': "Entrée supprimée",
    'ak.entryDeletedDesc': "L'entrée de la base de connaissances a été supprimée avec succès",
    'ak.failedDeleteEntry': "Échec de la suppression de l'entrée de la base de connaissances",
    'ak.nothingToExport': "Rien à exporter",
    'ak.nothingToExportDesc': "Il n'y a pas encore d'entrées de base de connaissances à exporter.",
    'ak.kbExportedDesc': "Base de connaissances exportée au format JSON.",
    'ak.importUnavailableDesc': "L'importation sera disponible dans une prochaine version.",
    'ak.loadingKb': "Chargement de la base de connaissances...",
    'ak.kbManagementTitle': "Gestion de la base de connaissances",
    'ak.kbManagementSubtitle': "Gérez les entrées de connaissances de l'IA et le contenu d'entraînement",
    'ak.entriesBadge': "{count} entrées",
    'ak.import': "Importer",
    'ak.export': "Exporter",
    'ak.totalEntries': "Total des entrées",
    'ak.avgSuccessRate': "Taux de réussite moyen",
    'ak.totalUsage': "Utilisation totale",
    'ak.categories': "Catégories",
    'ak.tab.browseEntries': "Parcourir les entrées",
    'ak.tab.createEntry': "Créer une entrée",
    'ak.tab.analytics': "Analytique",
    'ak.searchKbPlaceholder': "Rechercher dans la base de connaissances...",
    'ak.allCategories': "Toutes les catégories",
    'ak.usedTimesUpdated': "Utilisé {count} fois · Dernière mise à jour {date}",
    'ak.resolutionStepsLabel': "Étapes de résolution :",
    'ak.noEntriesFound': "Aucune entrée trouvée",
    'ak.noEntriesDesc': "Essayez d'ajuster votre recherche ou créez une nouvelle entrée dans la base de connaissances",
    'ak.createKbEntry': "Créer une entrée dans la base de connaissances",
    'ak.entryTitleRequired': "Titre de l'entrée *",
    'ak.enterEntryTitle': "Saisissez le titre de l'entrée",
    'ak.contentRequired': "Contenu *",
    'ak.enterKbContent': "Saisissez le contenu de la base de connaissances",
    'ak.tagsCommaSeparated': "Étiquettes (séparées par des virgules)",
    'ak.tagsExamplePlaceholder': "mot de passe, réinitialiser, connexion, sécurité",
    'ak.resolutionStepsOnePerLine': "Étapes de résolution (une par ligne)",
    'ak.resolutionStepsPlaceholder': "Étape 1 : Aller à la page de connexion\nÉtape 2 : Cliquer sur mot de passe oublié\nÉtape 3 : Saisir l'adresse e-mail",
    'ak.saveEntry': "Enregistrer l'entrée",
    'ak.kbAnalytics': "Analytique de la base de connaissances",
    'ak.performanceOverview': "Aperçu des performances",
    'ak.overallSuccessRate': "Taux de réussite global",
    'ak.knowledgeCoverage': "Couverture des connaissances",
    'ak.usageFrequency': "Fréquence d'utilisation",
    'ak.topPerformingEntries': "Entrées les plus performantes",
    'ak.viewDetailedAnalytics': "Voir l'analytique détaillée",
    'ak.editKbEntry': "Modifier l'entrée de la base de connaissances",
    'ak.editKbEntryDesc': "Modifiez les détails de l'entrée de la base de connaissances",
    'ak.updateEntry': "Mettre à jour l'entrée",
    'ak.areYouSure': "Êtes-vous sûr ?",
    'ak.deleteConfirmPrefix': "Cette action est irréversible. Elle supprimera définitivement l'entrée de la base de connaissances",
    'ak.deleteConfirmSuffix': "et supprimera toutes les données associées.",
    'ak.deleteEntry': "Supprimer l'entrée",
    'ak.detailedKbAnalytics': "Analytique détaillée de la base de connaissances",
    'ak.detailedKbAnalyticsDesc': "Analyse complète des performances et des perspectives de la base de connaissances",
    'ak.categoryPerformanceAnalysis': "Analyse des performances par catégorie",
    'ak.entriesLower': "{count} entrées",
    'ak.successRate': "Taux de réussite",
    'ak.usage': "Utilisation",
    'ak.aiPerformanceInsights': "Perspectives de performances de l'IA",
    'ak.highPerformingContent': "Contenu très performant",
    'ak.entriesAbove90': "{count} entrées ont des taux de réussite supérieurs à 90 %",
    'ak.optimizationOpportunities': "Opportunités d'optimisation",
    'ak.entriesBenefitOptimization': "{count} entrées pourraient bénéficier d'une optimisation",
    'ak.usageTrendsRecommendations': "Tendances d'utilisation et recommandations",
    'ak.mostUsedEntry': "Entrée la plus utilisée :",
    'ak.uses': "utilisations",
    'ak.bestPerformingEntry': "Entrée la plus performante :",
    'ak.success': "réussite",
    'ak.aiRecommendations': "Recommandations de l'IA :",
    'ak.recExpandCategories': "Envisagez d'enrichir les catégories performantes avec davantage d'entrées",
    'ak.recReviewLowSuccess': "Révisez et optimisez les entrées ayant des taux de réussite inférieurs à 80 %",
    'ak.recAddResolutionSteps': "Ajoutez davantage d'étapes de résolution aux entrées fréquemment utilisées",
    'ak.recMonitorFeedback': "Surveillez les commentaires des clients pour améliorer le contenu",
    'ak.failedLoadAnalytics': "Échec du chargement des données d'analytique de l'IA",
    'ak.analyticsExportedDesc': "Les données d'analytique de l'IA ont été exportées avec succès",
    'ak.loadingAnalytics': "Chargement de l'analytique de l'IA...",
    'ak.analyticsDashboardTitle': "Tableau de bord d'analytique de l'IA",
    'ak.analyticsDashboardSubtitle': "Métriques et perspectives complètes des performances de l'IA",
    'ak.range.24h': "Dernières 24 h",
    'ak.range.7d': "7 derniers jours",
    'ak.range.30d': "30 derniers jours",
    'ak.range.90d': "90 derniers jours",
    'ak.lastUpdated': "Dernière mise à jour : {time}",
    'ak.totalConversations': "Total des conversations",
    'ak.aiHandled': "Traitées par l'IA",
    'ak.autoResolutionRate': "Taux de résolution automatique",
    'ak.currentPeriod': "période actuelle",
    'ak.avgResponseTime': "Temps de réponse moyen",
    'ak.customerSatisfaction': "Satisfaction client",
    'ak.tab.categoryAnalysis': "Analyse par catégorie",
    'ak.tab.trendsPatterns': "Tendances et modèles",
    'ak.tab.aiInsights': "Perspectives de l'IA",
    'ak.resolutionPerformance': "Performances de résolution",
    'ak.successfulResolutions': "Résolutions réussies",
    'ak.escalationRate': "Taux d'escalade",
    'ak.aiConfidenceScore': "Score de confiance de l'IA",
    'ak.hourlyPerformance': "Performances horaires",
    'ak.conversationsCount': "{count} conversations",
    'ak.performanceByCategory': "Performances par catégorie",
    'ak.performanceTrends': "Tendances de performances",
    'ak.trendComparisonsUnavailable': "Les comparaisons de tendances ne sont pas encore disponibles",
    'ak.trendComparisonsDesc': "Les tendances d'une période à l'autre seront disponibles dans une prochaine version.",
    'ak.usagePatterns': "Modèles d'utilisation",
    'ak.peakHours': "Heures de pointe",
    'ak.mostCommonIssues': "Problèmes les plus courants",
    'ak.resolutionTime': "Temps de résolution",
    'ak.average': "Moyenne",
    'ak.aiInsightsUnavailable': "Les perspectives de performances de l'IA ne sont pas encore disponibles",
    'ak.aiInsightsDesc': "L'analyse automatique des perspectives sera disponible dans une prochaine version.",
    'ak.bestPracticeGuidance': "Conseils de bonnes pratiques",
    'ak.bestPracticeNote': "Conseils généraux pour améliorer les performances de l'IA — non dérivés de vos données en direct.",
    'ak.immediateActions': "Actions immédiates",
    'ak.immediate1': "Ajouter davantage d'entrées de support technique à la base de connaissances",
    'ak.immediate2': "Examiner les conversations escaladées pour l'analyse des modèles",
    'ak.immediate3': "Mettre à jour les données d'entraînement pour les scénarios de facturation complexes",
    'ak.strategicImprovements': "Améliorations stratégiques",
    'ak.strategic1': "Mettre en œuvre l'analyse des sentiments pour un meilleur acheminement",
    'ak.strategic2': "Ajouter la prise en charge multilingue pour les clients internationaux",
    'ak.strategic3': "Développer des capacités de détection proactive des problèmes",
    'ak.teamDevelopment': "Développement de l'équipe",
    'ak.team1': "Former les agents sur les cas escaladés par l'IA",
    'ak.team2': "Créer des boucles de rétroaction pour l'amélioration continue",
    'ak.team3': "Développer des flux de collaboration entre l'IA et les humains",
  },
  pt: {
    'ak.notAvailableYet': "Ainda não disponível",
    'ak.missingInformation': "Informações faltando",
    'ak.exportComplete': "Exportação concluída",
    'ak.cat.general': "Geral",
    'ak.cat.billing': "Faturamento",
    'ak.cat.technical': "Técnico",
    'ak.cat.account': "Conta",
    'ak.cat.product': "Produto",
    'ak.cat.shipping': "Envio",
    'ak.trainingUnavailableDesc': "O treinamento do modelo de IA estará disponível em uma versão futura.",
    'ak.fillRequiredFields': "Por favor, preencha todos os campos obrigatórios",
    'ak.saveScenariosUnavailableDesc': "Salvar cenários de treinamento estará disponível em uma versão futura.",
    'ak.missingFeedback': "Comentário faltando",
    'ak.provideFeedbackContent': "Por favor, forneça o conteúdo do comentário",
    'ak.submitFeedbackUnavailableDesc': "O envio de comentários de IA estará disponível em uma versão futura.",
    'ak.loadingLearningCenter': "Carregando central de aprendizado...",
    'ak.learningCenterTitle': "Central de Aprendizado de IA",
    'ak.learningCenterSubtitle': "Treine, melhore e otimize o desempenho da IA",
    'ak.trainingActive': "Treinamento ativo",
    'ak.ready': "Pronto",
    'ak.startTraining': "Iniciar treinamento",
    'ak.pauseTraining': "Pausar treinamento",
    'ak.modelAccuracy': "Precisão do modelo",
    'ak.learningRate': "Taxa de aprendizado",
    'ak.trainingProgress': "Progresso do treinamento",
    'ak.lastTraining': "Último treinamento",
    'ak.tab.customerFeedback': "Comentários dos clientes",
    'ak.tab.trainingScenarios': "Cenários de treinamento",
    'ak.tab.learningOpportunities': "Oportunidades de aprendizado",
    'ak.tab.modelPerformance': "Desempenho do modelo",
    'ak.recentCustomerFeedback': "Comentários recentes dos clientes",
    'ak.addFeedback': "Adicionar comentário",
    'ak.submitAiFeedback': "Enviar comentário de IA",
    'ak.submitAiFeedbackDesc': "Ajude a melhorar nossa IA fornecendo comentários sobre interações recentes",
    'ak.conversationIdOptional': "ID da conversa (opcional)",
    'ak.enterConversationId': "Insira o ID da conversa",
    'ak.rating': "Avaliação",
    'ak.feedbackRequired': "Comentário *",
    'ak.feedbackPlaceholder': "Descreva sua experiência com a resposta da IA...",
    'ak.suggestionsForImprovement': "Sugestões de melhoria",
    'ak.suggestionsPlaceholder': "Como a resposta da IA poderia ser melhorada?",
    'ak.submitFeedback': "Enviar comentário",
    'ak.noFeedbackYet': "Ainda não há comentários",
    'ak.noFeedbackDesc': "Os comentários dos clientes sobre interações de IA aparecerão aqui em uma versão futura.",
    'ak.needsReview': "Requer revisão",
    'ak.conversationLabel': "Conversa:",
    'ak.importScenarios': "Importar cenários",
    'ak.importTrainingScenarios': "Importar cenários de treinamento",
    'ak.importScenariosDesc': "Envie um arquivo JSON ou CSV com cenários de treinamento",
    'ak.importDropHint': "Arraste e solte ou selecione um arquivo para importar cenários de treinamento.",
    'ak.addScenario': "Adicionar cenário",
    'ak.createTrainingScenario': "Criar cenário de treinamento",
    'ak.createTrainingScenarioDesc': "Adicione um novo cenário de treinamento para melhorar as respostas da IA",
    'ak.scenarioTitleRequired': "Título do cenário *",
    'ak.enterScenarioTitle': "Insira o título do cenário",
    'ak.category': "Categoria",
    'ak.scenarioCatGeneral': "Suporte geral",
    'ak.customerScenarioRequired': "Cenário do cliente *",
    'ak.customerScenarioPlaceholder': "Descreva a situação ou pergunta do cliente...",
    'ak.expectedAiResponseRequired': "Resposta esperada da IA *",
    'ak.expectedAiResponsePlaceholder': "O que a IA deveria responder neste cenário?",
    'ak.difficultyLevel': "Nível de dificuldade",
    'ak.difficulty.easy': "Fácil",
    'ak.difficulty.medium': "Médio",
    'ak.difficulty.hard': "Difícil",
    'ak.difficulty.expert': "Especialista",
    'ak.tags': "Tags",
    'ak.tagsCommaPlaceholder': "Tags separadas por vírgulas",
    'ak.noScenariosYet': "Ainda não há cenários de treinamento",
    'ak.noScenariosDesc': "Os cenários de treinamento salvos aparecerão aqui em uma versão futura.",
    'ak.scenarioLabel': "Cenário:",
    'ak.expectedResponseLabel': "Resposta esperada:",
    'ak.noOpportunitiesYet': "Ainda não há oportunidades de aprendizado",
    'ak.noOpportunitiesDesc': "A detecção automática de oportunidades de aprendizado estará disponível em uma versão futura.",
    'ak.prioritySuffix': "prioridade",
    'ak.recommendedAction': "Ação recomendada:",
    'ak.expectedImpact': "Impacto esperado:",
    'ak.implement': "Implementar",
    'ak.schedule': "Agendar",
    'ak.modelPerformanceAnalytics': "Análise de desempenho do modelo",
    'ak.aiModelPerformance': "Desempenho do modelo de IA",
    'ak.learningProgress': "Progresso do aprendizado",
    'ak.trainingStatus': "Status do treinamento",
    'ak.trainingStatusFallback': "A resolução automática por IA é executada em um LLM hospedado; nenhuma métrica de treinamento de modelo local é registrada.",
    'ak.aiSolutionsPending': "Soluções de IA pendentes de revisão",
    'ak.approvedIntoSolutionBank': "Aprovadas no Banco de soluções",
    'ak.trainingAchievements': "Conquistas de treinamento",
    'ak.noAchievementsYet': "Ainda não há conquistas de treinamento",
    'ak.noAchievementsDesc': "Os marcos aparecerão aqui assim que o treinamento do modelo estiver ativo.",
    'ak.exportReportUnavailableDesc': "A exportação do relatório de desempenho estará disponível em uma versão futura.",
    'ak.exportPerformanceReport': "Exportar relatório de desempenho",
    'ak.failedLoadKb': "Falha ao carregar as entradas da base de conhecimento",
    'ak.fillTitleContent': "Por favor, preencha o título e o conteúdo",
    'ak.entryCreated': "Entrada criada",
    'ak.entryCreatedDesc': "A entrada da base de conhecimento foi criada com sucesso",
    'ak.failedCreateEntry': "Falha ao criar a entrada da base de conhecimento",
    'ak.entryUpdated': "Entrada atualizada",
    'ak.entryUpdatedDesc': "A entrada da base de conhecimento foi atualizada com sucesso",
    'ak.failedUpdateEntry': "Falha ao atualizar a entrada da base de conhecimento",
    'ak.entryDeleted': "Entrada excluída",
    'ak.entryDeletedDesc': "A entrada da base de conhecimento foi excluída com sucesso",
    'ak.failedDeleteEntry': "Falha ao excluir a entrada da base de conhecimento",
    'ak.nothingToExport': "Nada para exportar",
    'ak.nothingToExportDesc': "Ainda não há entradas da base de conhecimento para exportar.",
    'ak.kbExportedDesc': "Base de conhecimento exportada como JSON.",
    'ak.importUnavailableDesc': "A importação estará disponível em uma versão futura.",
    'ak.loadingKb': "Carregando base de conhecimento...",
    'ak.kbManagementTitle': "Gerenciamento da base de conhecimento",
    'ak.kbManagementSubtitle': "Gerencie as entradas de conhecimento de IA e o conteúdo de treinamento",
    'ak.entriesBadge': "{count} entradas",
    'ak.import': "Importar",
    'ak.export': "Exportar",
    'ak.totalEntries': "Total de entradas",
    'ak.avgSuccessRate': "Taxa média de sucesso",
    'ak.totalUsage': "Uso total",
    'ak.categories': "Categorias",
    'ak.tab.browseEntries': "Explorar entradas",
    'ak.tab.createEntry': "Criar entrada",
    'ak.tab.analytics': "Análises",
    'ak.searchKbPlaceholder': "Pesquisar na base de conhecimento...",
    'ak.allCategories': "Todas as categorias",
    'ak.usedTimesUpdated': "Usado {count} vezes · Última atualização {date}",
    'ak.resolutionStepsLabel': "Etapas de resolução:",
    'ak.noEntriesFound': "Nenhuma entrada encontrada",
    'ak.noEntriesDesc': "Tente ajustar sua pesquisa ou crie uma nova entrada na base de conhecimento",
    'ak.createKbEntry': "Criar entrada na base de conhecimento",
    'ak.entryTitleRequired': "Título da entrada *",
    'ak.enterEntryTitle': "Insira o título da entrada",
    'ak.contentRequired': "Conteúdo *",
    'ak.enterKbContent': "Insira o conteúdo da base de conhecimento",
    'ak.tagsCommaSeparated': "Tags (separadas por vírgulas)",
    'ak.tagsExamplePlaceholder': "senha, redefinir, login, segurança",
    'ak.resolutionStepsOnePerLine': "Etapas de resolução (uma por linha)",
    'ak.resolutionStepsPlaceholder': "Etapa 1: Ir para a página de login\nEtapa 2: Clicar em esqueci a senha\nEtapa 3: Inserir o endereço de e-mail",
    'ak.saveEntry': "Salvar entrada",
    'ak.kbAnalytics': "Análises da base de conhecimento",
    'ak.performanceOverview': "Visão geral do desempenho",
    'ak.overallSuccessRate': "Taxa de sucesso geral",
    'ak.knowledgeCoverage': "Cobertura de conhecimento",
    'ak.usageFrequency': "Frequência de uso",
    'ak.topPerformingEntries': "Entradas de melhor desempenho",
    'ak.viewDetailedAnalytics': "Ver análises detalhadas",
    'ak.editKbEntry': "Editar entrada da base de conhecimento",
    'ak.editKbEntryDesc': "Modifique os detalhes da entrada da base de conhecimento",
    'ak.updateEntry': "Atualizar entrada",
    'ak.areYouSure': "Tem certeza?",
    'ak.deleteConfirmPrefix': "Esta ação não pode ser desfeita. Isso excluirá permanentemente a entrada da base de conhecimento",
    'ak.deleteConfirmSuffix': "e removerá todos os dados associados.",
    'ak.deleteEntry': "Excluir entrada",
    'ak.detailedKbAnalytics': "Análises detalhadas da base de conhecimento",
    'ak.detailedKbAnalyticsDesc': "Análise abrangente do desempenho e das perspectivas da base de conhecimento",
    'ak.categoryPerformanceAnalysis': "Análise de desempenho por categoria",
    'ak.entriesLower': "{count} entradas",
    'ak.successRate': "Taxa de sucesso",
    'ak.usage': "Uso",
    'ak.aiPerformanceInsights': "Perspectivas de desempenho de IA",
    'ak.highPerformingContent': "Conteúdo de alto desempenho",
    'ak.entriesAbove90': "{count} entradas têm taxas de sucesso acima de 90%",
    'ak.optimizationOpportunities': "Oportunidades de otimização",
    'ak.entriesBenefitOptimization': "{count} entradas poderiam se beneficiar de otimização",
    'ak.usageTrendsRecommendations': "Tendências de uso e recomendações",
    'ak.mostUsedEntry': "Entrada mais usada:",
    'ak.uses': "usos",
    'ak.bestPerformingEntry': "Entrada de melhor desempenho:",
    'ak.success': "sucesso",
    'ak.aiRecommendations': "Recomendações de IA:",
    'ak.recExpandCategories': "Considere expandir as categorias de alto desempenho com mais entradas",
    'ak.recReviewLowSuccess': "Revise e otimize as entradas com taxas de sucesso abaixo de 80%",
    'ak.recAddResolutionSteps': "Adicione mais etapas de resolução às entradas usadas com frequência",
    'ak.recMonitorFeedback': "Monitore os comentários dos clientes para melhorar o conteúdo",
    'ak.failedLoadAnalytics': "Falha ao carregar os dados de análise de IA",
    'ak.analyticsExportedDesc': "Os dados de análise de IA foram exportados com sucesso",
    'ak.loadingAnalytics': "Carregando análises de IA...",
    'ak.analyticsDashboardTitle': "Painel de análises de IA",
    'ak.analyticsDashboardSubtitle': "Métricas e perspectivas abrangentes de desempenho de IA",
    'ak.range.24h': "Últimas 24h",
    'ak.range.7d': "Últimos 7 dias",
    'ak.range.30d': "Últimos 30 dias",
    'ak.range.90d': "Últimos 90 dias",
    'ak.lastUpdated': "Última atualização: {time}",
    'ak.totalConversations': "Total de conversas",
    'ak.aiHandled': "Tratadas pela IA",
    'ak.autoResolutionRate': "Taxa de resolução automática",
    'ak.currentPeriod': "período atual",
    'ak.avgResponseTime': "Tempo médio de resposta",
    'ak.customerSatisfaction': "Satisfação do cliente",
    'ak.tab.categoryAnalysis': "Análise por categoria",
    'ak.tab.trendsPatterns': "Tendências e padrões",
    'ak.tab.aiInsights': "Perspectivas de IA",
    'ak.resolutionPerformance': "Desempenho de resolução",
    'ak.successfulResolutions': "Resoluções bem-sucedidas",
    'ak.escalationRate': "Taxa de escalação",
    'ak.aiConfidenceScore': "Pontuação de confiança da IA",
    'ak.hourlyPerformance': "Desempenho por hora",
    'ak.conversationsCount': "{count} conversas",
    'ak.performanceByCategory': "Desempenho por categoria",
    'ak.performanceTrends': "Tendências de desempenho",
    'ak.trendComparisonsUnavailable': "As comparações de tendências ainda não estão disponíveis",
    'ak.trendComparisonsDesc': "As tendências período a período estarão disponíveis em uma versão futura.",
    'ak.usagePatterns': "Padrões de uso",
    'ak.peakHours': "Horários de pico",
    'ak.mostCommonIssues': "Problemas mais comuns",
    'ak.resolutionTime': "Tempo de resolução",
    'ak.average': "Média",
    'ak.aiInsightsUnavailable': "As perspectivas de desempenho de IA ainda não estão disponíveis",
    'ak.aiInsightsDesc': "A análise automática de perspectivas estará disponível em uma versão futura.",
    'ak.bestPracticeGuidance': "Orientação de boas práticas",
    'ak.bestPracticeNote': "Orientação geral para melhorar o desempenho da IA — não derivada dos seus dados em tempo real.",
    'ak.immediateActions': "Ações imediatas",
    'ak.immediate1': "Adicionar mais entradas de suporte técnico à base de conhecimento",
    'ak.immediate2': "Revisar conversas escaladas para análise de padrões",
    'ak.immediate3': "Atualizar os dados de treinamento para cenários de faturamento complexos",
    'ak.strategicImprovements': "Melhorias estratégicas",
    'ak.strategic1': "Implementar análise de sentimento para melhor roteamento",
    'ak.strategic2': "Adicionar suporte multilíngue para clientes globais",
    'ak.strategic3': "Desenvolver capacidades de detecção proativa de problemas",
    'ak.teamDevelopment': "Desenvolvimento da equipe",
    'ak.team1': "Treinar agentes em casos escalados pela IA",
    'ak.team2': "Criar ciclos de feedback para melhoria contínua",
    'ak.team3': "Desenvolver fluxos de trabalho de colaboração entre IA e humanos",
  },
  de: {
    'ak.notAvailableYet': "Noch nicht verfügbar",
    'ak.missingInformation': "Fehlende Informationen",
    'ak.exportComplete': "Export abgeschlossen",
    'ak.cat.general': "Allgemein",
    'ak.cat.billing': "Abrechnung",
    'ak.cat.technical': "Technisch",
    'ak.cat.account': "Konto",
    'ak.cat.product': "Produkt",
    'ak.cat.shipping': "Versand",
    'ak.trainingUnavailableDesc': "Das Training des KI-Modells wird in einer kommenden Version verfügbar sein.",
    'ak.fillRequiredFields': "Bitte füllen Sie alle Pflichtfelder aus",
    'ak.saveScenariosUnavailableDesc': "Das Speichern von Trainingsszenarien wird in einer kommenden Version verfügbar sein.",
    'ak.missingFeedback': "Fehlendes Feedback",
    'ak.provideFeedbackContent': "Bitte geben Sie den Feedback-Inhalt an",
    'ak.submitFeedbackUnavailableDesc': "Das Senden von KI-Feedback wird in einer kommenden Version verfügbar sein.",
    'ak.loadingLearningCenter': "Lernzentrum wird geladen...",
    'ak.learningCenterTitle': "KI-Lernzentrum",
    'ak.learningCenterSubtitle': "Trainieren, verbessern und optimieren Sie die KI-Leistung",
    'ak.trainingActive': "Training aktiv",
    'ak.ready': "Bereit",
    'ak.startTraining': "Training starten",
    'ak.pauseTraining': "Training pausieren",
    'ak.modelAccuracy': "Modellgenauigkeit",
    'ak.learningRate': "Lernrate",
    'ak.trainingProgress': "Trainingsfortschritt",
    'ak.lastTraining': "Letztes Training",
    'ak.tab.customerFeedback': "Kundenfeedback",
    'ak.tab.trainingScenarios': "Trainingsszenarien",
    'ak.tab.learningOpportunities': "Lernmöglichkeiten",
    'ak.tab.modelPerformance': "Modellleistung",
    'ak.recentCustomerFeedback': "Aktuelles Kundenfeedback",
    'ak.addFeedback': "Feedback hinzufügen",
    'ak.submitAiFeedback': "KI-Feedback senden",
    'ak.submitAiFeedbackDesc': "Helfen Sie mit, unsere KI zu verbessern, indem Sie Feedback zu aktuellen Interaktionen geben",
    'ak.conversationIdOptional': "Konversations-ID (optional)",
    'ak.enterConversationId': "Konversations-ID eingeben",
    'ak.rating': "Bewertung",
    'ak.feedbackRequired': "Feedback *",
    'ak.feedbackPlaceholder': "Beschreiben Sie Ihre Erfahrung mit der KI-Antwort...",
    'ak.suggestionsForImprovement': "Verbesserungsvorschläge",
    'ak.suggestionsPlaceholder': "Wie könnte die KI-Antwort verbessert werden?",
    'ak.submitFeedback': "Feedback senden",
    'ak.noFeedbackYet': "Noch kein Feedback",
    'ak.noFeedbackDesc': "Kundenfeedback zu KI-Interaktionen wird hier in einer kommenden Version angezeigt.",
    'ak.needsReview': "Überprüfung erforderlich",
    'ak.conversationLabel': "Konversation:",
    'ak.importScenarios': "Szenarien importieren",
    'ak.importTrainingScenarios': "Trainingsszenarien importieren",
    'ak.importScenariosDesc': "Laden Sie eine JSON- oder CSV-Datei mit Trainingsszenarien hoch",
    'ak.importDropHint': "Ziehen Sie eine Datei per Drag-and-Drop hierher oder wählen Sie sie aus, um Trainingsszenarien zu importieren.",
    'ak.addScenario': "Szenario hinzufügen",
    'ak.createTrainingScenario': "Trainingsszenario erstellen",
    'ak.createTrainingScenarioDesc': "Fügen Sie ein neues Trainingsszenario hinzu, um die KI-Antworten zu verbessern",
    'ak.scenarioTitleRequired': "Szenariotitel *",
    'ak.enterScenarioTitle': "Szenariotitel eingeben",
    'ak.category': "Kategorie",
    'ak.scenarioCatGeneral': "Allgemeiner Support",
    'ak.customerScenarioRequired': "Kundenszenario *",
    'ak.customerScenarioPlaceholder': "Beschreiben Sie die Situation oder Frage des Kunden...",
    'ak.expectedAiResponseRequired': "Erwartete KI-Antwort *",
    'ak.expectedAiResponsePlaceholder': "Was sollte die KI in diesem Szenario antworten?",
    'ak.difficultyLevel': "Schwierigkeitsgrad",
    'ak.difficulty.easy': "Einfach",
    'ak.difficulty.medium': "Mittel",
    'ak.difficulty.hard': "Schwer",
    'ak.difficulty.expert': "Experte",
    'ak.tags': "Tags",
    'ak.tagsCommaPlaceholder': "Durch Kommas getrennte Tags",
    'ak.noScenariosYet': "Noch keine Trainingsszenarien",
    'ak.noScenariosDesc': "Gespeicherte Trainingsszenarien werden hier in einer kommenden Version angezeigt.",
    'ak.scenarioLabel': "Szenario:",
    'ak.expectedResponseLabel': "Erwartete Antwort:",
    'ak.noOpportunitiesYet': "Noch keine Lernmöglichkeiten",
    'ak.noOpportunitiesDesc': "Die automatische Erkennung von Lernmöglichkeiten wird in einer kommenden Version verfügbar sein.",
    'ak.prioritySuffix': "Priorität",
    'ak.recommendedAction': "Empfohlene Maßnahme:",
    'ak.expectedImpact': "Erwartete Auswirkung:",
    'ak.implement': "Umsetzen",
    'ak.schedule': "Planen",
    'ak.modelPerformanceAnalytics': "Analyse der Modellleistung",
    'ak.aiModelPerformance': "KI-Modellleistung",
    'ak.learningProgress': "Lernfortschritt",
    'ak.trainingStatus': "Trainingsstatus",
    'ak.trainingStatusFallback': "Die automatische KI-Lösung läuft auf einem gehosteten LLM; es werden keine lokalen Modelltrainingsmetriken erfasst.",
    'ak.aiSolutionsPending': "KI-Lösungen zur Überprüfung ausstehend",
    'ak.approvedIntoSolutionBank': "In die Lösungsdatenbank genehmigt",
    'ak.trainingAchievements': "Trainingserfolge",
    'ak.noAchievementsYet': "Noch keine Trainingserfolge",
    'ak.noAchievementsDesc': "Meilensteine werden hier angezeigt, sobald das Modelltraining aktiv ist.",
    'ak.exportReportUnavailableDesc': "Der Export des Leistungsberichts wird in einer kommenden Version verfügbar sein.",
    'ak.exportPerformanceReport': "Leistungsbericht exportieren",
    'ak.failedLoadKb': "Fehler beim Laden der Wissensdatenbank-Einträge",
    'ak.fillTitleContent': "Bitte füllen Sie den Titel und den Inhalt aus",
    'ak.entryCreated': "Eintrag erstellt",
    'ak.entryCreatedDesc': "Der Wissensdatenbank-Eintrag wurde erfolgreich erstellt",
    'ak.failedCreateEntry': "Fehler beim Erstellen des Wissensdatenbank-Eintrags",
    'ak.entryUpdated': "Eintrag aktualisiert",
    'ak.entryUpdatedDesc': "Der Wissensdatenbank-Eintrag wurde erfolgreich aktualisiert",
    'ak.failedUpdateEntry': "Fehler beim Aktualisieren des Wissensdatenbank-Eintrags",
    'ak.entryDeleted': "Eintrag gelöscht",
    'ak.entryDeletedDesc': "Der Wissensdatenbank-Eintrag wurde erfolgreich gelöscht",
    'ak.failedDeleteEntry': "Fehler beim Löschen des Wissensdatenbank-Eintrags",
    'ak.nothingToExport': "Nichts zu exportieren",
    'ak.nothingToExportDesc': "Es gibt noch keine Wissensdatenbank-Einträge zum Exportieren.",
    'ak.kbExportedDesc': "Wissensdatenbank als JSON exportiert.",
    'ak.importUnavailableDesc': "Der Import wird in einer kommenden Version verfügbar sein.",
    'ak.loadingKb': "Wissensdatenbank wird geladen...",
    'ak.kbManagementTitle': "Wissensdatenbank-Verwaltung",
    'ak.kbManagementSubtitle': "Verwalten Sie KI-Wissenseinträge und Trainingsinhalte",
    'ak.entriesBadge': "{count} Einträge",
    'ak.import': "Importieren",
    'ak.export': "Exportieren",
    'ak.totalEntries': "Einträge insgesamt",
    'ak.avgSuccessRate': "Durchschn. Erfolgsrate",
    'ak.totalUsage': "Gesamtnutzung",
    'ak.categories': "Kategorien",
    'ak.tab.browseEntries': "Einträge durchsuchen",
    'ak.tab.createEntry': "Eintrag erstellen",
    'ak.tab.analytics': "Analysen",
    'ak.searchKbPlaceholder': "Wissensdatenbank durchsuchen...",
    'ak.allCategories': "Alle Kategorien",
    'ak.usedTimesUpdated': "{count} Mal verwendet · Zuletzt aktualisiert {date}",
    'ak.resolutionStepsLabel': "Lösungsschritte:",
    'ak.noEntriesFound': "Keine Einträge gefunden",
    'ak.noEntriesDesc': "Versuchen Sie, Ihre Suche anzupassen, oder erstellen Sie einen neuen Wissensdatenbank-Eintrag",
    'ak.createKbEntry': "Wissensdatenbank-Eintrag erstellen",
    'ak.entryTitleRequired': "Eintragstitel *",
    'ak.enterEntryTitle': "Eintragstitel eingeben",
    'ak.contentRequired': "Inhalt *",
    'ak.enterKbContent': "Geben Sie den Wissensdatenbank-Inhalt ein",
    'ak.tagsCommaSeparated': "Tags (durch Kommas getrennt)",
    'ak.tagsExamplePlaceholder': "Passwort, zurücksetzen, Anmeldung, Sicherheit",
    'ak.resolutionStepsOnePerLine': "Lösungsschritte (einer pro Zeile)",
    'ak.resolutionStepsPlaceholder': "Schritt 1: Zur Anmeldeseite gehen\nSchritt 2: Auf Passwort vergessen klicken\nSchritt 3: E-Mail-Adresse eingeben",
    'ak.saveEntry': "Eintrag speichern",
    'ak.kbAnalytics': "Wissensdatenbank-Analysen",
    'ak.performanceOverview': "Leistungsübersicht",
    'ak.overallSuccessRate': "Gesamterfolgsrate",
    'ak.knowledgeCoverage': "Wissensabdeckung",
    'ak.usageFrequency': "Nutzungshäufigkeit",
    'ak.topPerformingEntries': "Leistungsstärkste Einträge",
    'ak.viewDetailedAnalytics': "Detaillierte Analysen anzeigen",
    'ak.editKbEntry': "Wissensdatenbank-Eintrag bearbeiten",
    'ak.editKbEntryDesc': "Ändern Sie die Details des Wissensdatenbank-Eintrags",
    'ak.updateEntry': "Eintrag aktualisieren",
    'ak.areYouSure': "Sind Sie sicher?",
    'ak.deleteConfirmPrefix': "Diese Aktion kann nicht rückgängig gemacht werden. Dadurch wird der Wissensdatenbank-Eintrag dauerhaft gelöscht",
    'ak.deleteConfirmSuffix': "und alle zugehörigen Daten entfernt.",
    'ak.deleteEntry': "Eintrag löschen",
    'ak.detailedKbAnalytics': "Detaillierte Wissensdatenbank-Analysen",
    'ak.detailedKbAnalyticsDesc': "Umfassende Analyse der Leistung und Erkenntnisse der Wissensdatenbank",
    'ak.categoryPerformanceAnalysis': "Leistungsanalyse nach Kategorie",
    'ak.entriesLower': "{count} Einträge",
    'ak.successRate': "Erfolgsrate",
    'ak.usage': "Nutzung",
    'ak.aiPerformanceInsights': "KI-Leistungseinblicke",
    'ak.highPerformingContent': "Leistungsstarke Inhalte",
    'ak.entriesAbove90': "{count} Einträge haben Erfolgsraten über 90 %",
    'ak.optimizationOpportunities': "Optimierungsmöglichkeiten",
    'ak.entriesBenefitOptimization': "{count} Einträge könnten von einer Optimierung profitieren",
    'ak.usageTrendsRecommendations': "Nutzungstrends & Empfehlungen",
    'ak.mostUsedEntry': "Am häufigsten verwendeter Eintrag:",
    'ak.uses': "Verwendungen",
    'ak.bestPerformingEntry': "Leistungsstärkster Eintrag:",
    'ak.success': "Erfolg",
    'ak.aiRecommendations': "KI-Empfehlungen:",
    'ak.recExpandCategories': "Erwägen Sie, leistungsstarke Kategorien um weitere Einträge zu erweitern",
    'ak.recReviewLowSuccess': "Überprüfen und optimieren Sie Einträge mit Erfolgsraten unter 80 %",
    'ak.recAddResolutionSteps': "Fügen Sie häufig verwendeten Einträgen weitere Lösungsschritte hinzu",
    'ak.recMonitorFeedback': "Überwachen Sie das Kundenfeedback für Inhaltsverbesserungen",
    'ak.failedLoadAnalytics': "Fehler beim Laden der KI-Analysedaten",
    'ak.analyticsExportedDesc': "Die KI-Analysedaten wurden erfolgreich exportiert",
    'ak.loadingAnalytics': "KI-Analysen werden geladen...",
    'ak.analyticsDashboardTitle': "KI-Analyse-Dashboard",
    'ak.analyticsDashboardSubtitle': "Umfassende KI-Leistungsmetriken und -Einblicke",
    'ak.range.24h': "Letzte 24 Std.",
    'ak.range.7d': "Letzte 7 Tage",
    'ak.range.30d': "Letzte 30 Tage",
    'ak.range.90d': "Letzte 90 Tage",
    'ak.lastUpdated': "Zuletzt aktualisiert: {time}",
    'ak.totalConversations': "Konversationen insgesamt",
    'ak.aiHandled': "Von KI bearbeitet",
    'ak.autoResolutionRate': "Automatische Lösungsrate",
    'ak.currentPeriod': "aktueller Zeitraum",
    'ak.avgResponseTime': "Durchschn. Antwortzeit",
    'ak.customerSatisfaction': "Kundenzufriedenheit",
    'ak.tab.categoryAnalysis': "Kategorieanalyse",
    'ak.tab.trendsPatterns': "Trends & Muster",
    'ak.tab.aiInsights': "KI-Einblicke",
    'ak.resolutionPerformance': "Lösungsleistung",
    'ak.successfulResolutions': "Erfolgreiche Lösungen",
    'ak.escalationRate': "Eskalationsrate",
    'ak.aiConfidenceScore': "KI-Konfidenzwert",
    'ak.hourlyPerformance': "Stündliche Leistung",
    'ak.conversationsCount': "{count} Konversationen",
    'ak.performanceByCategory': "Leistung nach Kategorie",
    'ak.performanceTrends': "Leistungstrends",
    'ak.trendComparisonsUnavailable': "Trendvergleiche sind noch nicht verfügbar",
    'ak.trendComparisonsDesc': "Zeitraumvergleiche werden in einer kommenden Version verfügbar sein.",
    'ak.usagePatterns': "Nutzungsmuster",
    'ak.peakHours': "Stoßzeiten",
    'ak.mostCommonIssues': "Häufigste Probleme",
    'ak.resolutionTime': "Lösungszeit",
    'ak.average': "Durchschnitt",
    'ak.aiInsightsUnavailable': "KI-Leistungseinblicke sind noch nicht verfügbar",
    'ak.aiInsightsDesc': "Die automatische Einblicksanalyse wird in einer kommenden Version verfügbar sein.",
    'ak.bestPracticeGuidance': "Best-Practice-Leitfaden",
    'ak.bestPracticeNote': "Allgemeine Hinweise zur Verbesserung der KI-Leistung — nicht aus Ihren Live-Daten abgeleitet.",
    'ak.immediateActions': "Sofortige Maßnahmen",
    'ak.immediate1': "Weitere Einträge für technischen Support zur Wissensdatenbank hinzufügen",
    'ak.immediate2': "Eskalierte Konversationen zur Musteranalyse überprüfen",
    'ak.immediate3': "Trainingsdaten für komplexe Abrechnungsszenarien aktualisieren",
    'ak.strategicImprovements': "Strategische Verbesserungen",
    'ak.strategic1': "Sentiment-Analyse für besseres Routing implementieren",
    'ak.strategic2': "Mehrsprachige Unterstützung für globale Kunden hinzufügen",
    'ak.strategic3': "Proaktive Fähigkeiten zur Problemerkennung entwickeln",
    'ak.teamDevelopment': "Teamentwicklung",
    'ak.team1': "Agenten für KI-eskalierte Fälle schulen",
    'ak.team2': "Feedback-Schleifen für kontinuierliche Verbesserung erstellen",
    'ak.team3': "Workflows für die Zusammenarbeit von KI und Mensch entwickeln",
  },
  it: {
    'ak.notAvailableYet': "Non ancora disponibile",
    'ak.missingInformation': "Informazioni mancanti",
    'ak.exportComplete': "Esportazione completata",
    'ak.cat.general': "Generale",
    'ak.cat.billing': "Fatturazione",
    'ak.cat.technical': "Tecnico",
    'ak.cat.account': "Account",
    'ak.cat.product': "Prodotto",
    'ak.cat.shipping': "Spedizione",
    'ak.trainingUnavailableDesc': "L'addestramento del modello di IA sarà disponibile in una prossima versione.",
    'ak.fillRequiredFields': "Si prega di compilare tutti i campi obbligatori",
    'ak.saveScenariosUnavailableDesc': "Il salvataggio degli scenari di addestramento sarà disponibile in una prossima versione.",
    'ak.missingFeedback': "Feedback mancante",
    'ak.provideFeedbackContent': "Si prega di fornire il contenuto del feedback",
    'ak.submitFeedbackUnavailableDesc': "L'invio di feedback all'IA sarà disponibile in una prossima versione.",
    'ak.loadingLearningCenter': "Caricamento del centro di apprendimento...",
    'ak.learningCenterTitle': "Centro di apprendimento IA",
    'ak.learningCenterSubtitle': "Addestra, migliora e ottimizza le prestazioni dell'IA",
    'ak.trainingActive': "Addestramento attivo",
    'ak.ready': "Pronto",
    'ak.startTraining': "Avvia addestramento",
    'ak.pauseTraining': "Metti in pausa l'addestramento",
    'ak.modelAccuracy': "Precisione del modello",
    'ak.learningRate': "Tasso di apprendimento",
    'ak.trainingProgress': "Avanzamento dell'addestramento",
    'ak.lastTraining': "Ultimo addestramento",
    'ak.tab.customerFeedback': "Feedback dei clienti",
    'ak.tab.trainingScenarios': "Scenari di addestramento",
    'ak.tab.learningOpportunities': "Opportunità di apprendimento",
    'ak.tab.modelPerformance': "Prestazioni del modello",
    'ak.recentCustomerFeedback': "Feedback recenti dei clienti",
    'ak.addFeedback': "Aggiungi feedback",
    'ak.submitAiFeedback': "Invia feedback all'IA",
    'ak.submitAiFeedbackDesc': "Aiutaci a migliorare la nostra IA fornendo feedback sulle interazioni recenti",
    'ak.conversationIdOptional': "ID conversazione (facoltativo)",
    'ak.enterConversationId': "Inserisci l'ID conversazione",
    'ak.rating': "Valutazione",
    'ak.feedbackRequired': "Feedback *",
    'ak.feedbackPlaceholder': "Descrivi la tua esperienza con la risposta dell'IA...",
    'ak.suggestionsForImprovement': "Suggerimenti per il miglioramento",
    'ak.suggestionsPlaceholder': "Come potrebbe essere migliorata la risposta dell'IA?",
    'ak.submitFeedback': "Invia feedback",
    'ak.noFeedbackYet': "Nessun feedback ancora",
    'ak.noFeedbackDesc': "Il feedback dei clienti sulle interazioni con l'IA apparirà qui in una prossima versione.",
    'ak.needsReview': "Da rivedere",
    'ak.conversationLabel': "Conversazione:",
    'ak.importScenarios': "Importa scenari",
    'ak.importTrainingScenarios': "Importa scenari di addestramento",
    'ak.importScenariosDesc': "Carica un file JSON o CSV con scenari di addestramento",
    'ak.importDropHint': "Trascina e rilascia o seleziona un file per importare gli scenari di addestramento.",
    'ak.addScenario': "Aggiungi scenario",
    'ak.createTrainingScenario': "Crea scenario di addestramento",
    'ak.createTrainingScenarioDesc': "Aggiungi un nuovo scenario di addestramento per migliorare le risposte dell'IA",
    'ak.scenarioTitleRequired': "Titolo dello scenario *",
    'ak.enterScenarioTitle': "Inserisci il titolo dello scenario",
    'ak.category': "Categoria",
    'ak.scenarioCatGeneral': "Supporto generale",
    'ak.customerScenarioRequired': "Scenario del cliente *",
    'ak.customerScenarioPlaceholder': "Descrivi la situazione o la domanda del cliente...",
    'ak.expectedAiResponseRequired': "Risposta prevista dell'IA *",
    'ak.expectedAiResponsePlaceholder': "Cosa dovrebbe rispondere l'IA in questo scenario?",
    'ak.difficultyLevel': "Livello di difficoltà",
    'ak.difficulty.easy': "Facile",
    'ak.difficulty.medium': "Medio",
    'ak.difficulty.hard': "Difficile",
    'ak.difficulty.expert': "Esperto",
    'ak.tags': "Tag",
    'ak.tagsCommaPlaceholder': "Tag separati da virgole",
    'ak.noScenariosYet': "Nessuno scenario di addestramento ancora",
    'ak.noScenariosDesc': "Gli scenari di addestramento salvati appariranno qui in una prossima versione.",
    'ak.scenarioLabel': "Scenario:",
    'ak.expectedResponseLabel': "Risposta prevista:",
    'ak.noOpportunitiesYet': "Nessuna opportunità di apprendimento ancora",
    'ak.noOpportunitiesDesc': "Il rilevamento automatico delle opportunità di apprendimento sarà disponibile in una prossima versione.",
    'ak.prioritySuffix': "priorità",
    'ak.recommendedAction': "Azione consigliata:",
    'ak.expectedImpact': "Impatto previsto:",
    'ak.implement': "Implementa",
    'ak.schedule': "Pianifica",
    'ak.modelPerformanceAnalytics': "Analisi delle prestazioni del modello",
    'ak.aiModelPerformance': "Prestazioni del modello di IA",
    'ak.learningProgress': "Avanzamento dell'apprendimento",
    'ak.trainingStatus': "Stato dell'addestramento",
    'ak.trainingStatusFallback': "La risoluzione automatica dell'IA viene eseguita su un LLM ospitato; non vengono registrate metriche di addestramento del modello locale.",
    'ak.aiSolutionsPending': "Soluzioni IA in attesa di revisione",
    'ak.approvedIntoSolutionBank': "Approvate nella Banca delle soluzioni",
    'ak.trainingAchievements': "Traguardi di addestramento",
    'ak.noAchievementsYet': "Nessun traguardo di addestramento ancora",
    'ak.noAchievementsDesc': "I traguardi appariranno qui una volta che l'addestramento del modello sarà attivo.",
    'ak.exportReportUnavailableDesc': "L'esportazione del rapporto sulle prestazioni sarà disponibile in una prossima versione.",
    'ak.exportPerformanceReport': "Esporta rapporto sulle prestazioni",
    'ak.failedLoadKb': "Impossibile caricare le voci della base di conoscenza",
    'ak.fillTitleContent': "Si prega di compilare il titolo e il contenuto",
    'ak.entryCreated': "Voce creata",
    'ak.entryCreatedDesc': "La voce della base di conoscenza è stata creata con successo",
    'ak.failedCreateEntry': "Impossibile creare la voce della base di conoscenza",
    'ak.entryUpdated': "Voce aggiornata",
    'ak.entryUpdatedDesc': "La voce della base di conoscenza è stata aggiornata con successo",
    'ak.failedUpdateEntry': "Impossibile aggiornare la voce della base di conoscenza",
    'ak.entryDeleted': "Voce eliminata",
    'ak.entryDeletedDesc': "La voce della base di conoscenza è stata eliminata con successo",
    'ak.failedDeleteEntry': "Impossibile eliminare la voce della base di conoscenza",
    'ak.nothingToExport': "Niente da esportare",
    'ak.nothingToExportDesc': "Non ci sono ancora voci della base di conoscenza da esportare.",
    'ak.kbExportedDesc': "Base di conoscenza esportata come JSON.",
    'ak.importUnavailableDesc': "L'importazione sarà disponibile in una prossima versione.",
    'ak.loadingKb': "Caricamento della base di conoscenza...",
    'ak.kbManagementTitle': "Gestione della base di conoscenza",
    'ak.kbManagementSubtitle': "Gestisci le voci di conoscenza dell'IA e i contenuti di addestramento",
    'ak.entriesBadge': "{count} voci",
    'ak.import': "Importa",
    'ak.export': "Esporta",
    'ak.totalEntries': "Voci totali",
    'ak.avgSuccessRate': "Tasso di successo medio",
    'ak.totalUsage': "Utilizzo totale",
    'ak.categories': "Categorie",
    'ak.tab.browseEntries': "Sfoglia voci",
    'ak.tab.createEntry': "Crea voce",
    'ak.tab.analytics': "Analisi",
    'ak.searchKbPlaceholder': "Cerca nella base di conoscenza...",
    'ak.allCategories': "Tutte le categorie",
    'ak.usedTimesUpdated': "Utilizzato {count} volte · Ultimo aggiornamento {date}",
    'ak.resolutionStepsLabel': "Passaggi di risoluzione:",
    'ak.noEntriesFound': "Nessuna voce trovata",
    'ak.noEntriesDesc': "Prova a modificare la ricerca o crea una nuova voce nella base di conoscenza",
    'ak.createKbEntry': "Crea voce nella base di conoscenza",
    'ak.entryTitleRequired': "Titolo della voce *",
    'ak.enterEntryTitle': "Inserisci il titolo della voce",
    'ak.contentRequired': "Contenuto *",
    'ak.enterKbContent': "Inserisci il contenuto della base di conoscenza",
    'ak.tagsCommaSeparated': "Tag (separati da virgole)",
    'ak.tagsExamplePlaceholder': "password, reimposta, accesso, sicurezza",
    'ak.resolutionStepsOnePerLine': "Passaggi di risoluzione (uno per riga)",
    'ak.resolutionStepsPlaceholder': "Passaggio 1: Vai alla pagina di accesso\nPassaggio 2: Fai clic su password dimenticata\nPassaggio 3: Inserisci l'indirizzo e-mail",
    'ak.saveEntry': "Salva voce",
    'ak.kbAnalytics': "Analisi della base di conoscenza",
    'ak.performanceOverview': "Panoramica delle prestazioni",
    'ak.overallSuccessRate': "Tasso di successo complessivo",
    'ak.knowledgeCoverage': "Copertura della conoscenza",
    'ak.usageFrequency': "Frequenza di utilizzo",
    'ak.topPerformingEntries': "Voci con le migliori prestazioni",
    'ak.viewDetailedAnalytics': "Visualizza analisi dettagliate",
    'ak.editKbEntry': "Modifica voce della base di conoscenza",
    'ak.editKbEntryDesc': "Modifica i dettagli della voce della base di conoscenza",
    'ak.updateEntry': "Aggiorna voce",
    'ak.areYouSure': "Sei sicuro?",
    'ak.deleteConfirmPrefix': "Questa azione non può essere annullata. Eliminerà definitivamente la voce della base di conoscenza",
    'ak.deleteConfirmSuffix': "e rimuoverà tutti i dati associati.",
    'ak.deleteEntry': "Elimina voce",
    'ak.detailedKbAnalytics': "Analisi dettagliate della base di conoscenza",
    'ak.detailedKbAnalyticsDesc': "Analisi completa delle prestazioni e delle informazioni della base di conoscenza",
    'ak.categoryPerformanceAnalysis': "Analisi delle prestazioni per categoria",
    'ak.entriesLower': "{count} voci",
    'ak.successRate': "Tasso di successo",
    'ak.usage': "Utilizzo",
    'ak.aiPerformanceInsights': "Informazioni sulle prestazioni dell'IA",
    'ak.highPerformingContent': "Contenuti ad alte prestazioni",
    'ak.entriesAbove90': "{count} voci hanno tassi di successo superiori al 90%",
    'ak.optimizationOpportunities': "Opportunità di ottimizzazione",
    'ak.entriesBenefitOptimization': "{count} voci potrebbero beneficiare di un'ottimizzazione",
    'ak.usageTrendsRecommendations': "Tendenze di utilizzo e raccomandazioni",
    'ak.mostUsedEntry': "Voce più utilizzata:",
    'ak.uses': "utilizzi",
    'ak.bestPerformingEntry': "Voce con le migliori prestazioni:",
    'ak.success': "successo",
    'ak.aiRecommendations': "Raccomandazioni dell'IA:",
    'ak.recExpandCategories': "Valuta di ampliare le categorie ad alte prestazioni con più voci",
    'ak.recReviewLowSuccess': "Rivedi e ottimizza le voci con tassi di successo inferiori all'80%",
    'ak.recAddResolutionSteps': "Aggiungi più passaggi di risoluzione alle voci utilizzate di frequente",
    'ak.recMonitorFeedback': "Monitora il feedback dei clienti per migliorare i contenuti",
    'ak.failedLoadAnalytics': "Impossibile caricare i dati di analisi dell'IA",
    'ak.analyticsExportedDesc': "I dati di analisi dell'IA sono stati esportati con successo",
    'ak.loadingAnalytics': "Caricamento delle analisi dell'IA...",
    'ak.analyticsDashboardTitle': "Dashboard di analisi dell'IA",
    'ak.analyticsDashboardSubtitle': "Metriche e informazioni complete sulle prestazioni dell'IA",
    'ak.range.24h': "Ultime 24h",
    'ak.range.7d': "Ultimi 7 giorni",
    'ak.range.30d': "Ultimi 30 giorni",
    'ak.range.90d': "Ultimi 90 giorni",
    'ak.lastUpdated': "Ultimo aggiornamento: {time}",
    'ak.totalConversations': "Conversazioni totali",
    'ak.aiHandled': "Gestite dall'IA",
    'ak.autoResolutionRate': "Tasso di risoluzione automatica",
    'ak.currentPeriod': "periodo attuale",
    'ak.avgResponseTime': "Tempo di risposta medio",
    'ak.customerSatisfaction': "Soddisfazione del cliente",
    'ak.tab.categoryAnalysis': "Analisi per categoria",
    'ak.tab.trendsPatterns': "Tendenze e schemi",
    'ak.tab.aiInsights': "Informazioni sull'IA",
    'ak.resolutionPerformance': "Prestazioni di risoluzione",
    'ak.successfulResolutions': "Risoluzioni riuscite",
    'ak.escalationRate': "Tasso di escalation",
    'ak.aiConfidenceScore': "Punteggio di affidabilità dell'IA",
    'ak.hourlyPerformance': "Prestazioni orarie",
    'ak.conversationsCount': "{count} conversazioni",
    'ak.performanceByCategory': "Prestazioni per categoria",
    'ak.performanceTrends': "Tendenze delle prestazioni",
    'ak.trendComparisonsUnavailable': "I confronti delle tendenze non sono ancora disponibili",
    'ak.trendComparisonsDesc': "Le tendenze periodo su periodo saranno disponibili in una prossima versione.",
    'ak.usagePatterns': "Schemi di utilizzo",
    'ak.peakHours': "Ore di punta",
    'ak.mostCommonIssues': "Problemi più comuni",
    'ak.resolutionTime': "Tempo di risoluzione",
    'ak.average': "Media",
    'ak.aiInsightsUnavailable': "Le informazioni sulle prestazioni dell'IA non sono ancora disponibili",
    'ak.aiInsightsDesc': "L'analisi automatica delle informazioni sarà disponibile in una prossima versione.",
    'ak.bestPracticeGuidance': "Guida alle migliori pratiche",
    'ak.bestPracticeNote': "Indicazioni generali per migliorare le prestazioni dell'IA — non derivate dai tuoi dati in tempo reale.",
    'ak.immediateActions': "Azioni immediate",
    'ak.immediate1': "Aggiungi altre voci di supporto tecnico alla base di conoscenza",
    'ak.immediate2': "Rivedi le conversazioni inoltrate per l'analisi degli schemi",
    'ak.immediate3': "Aggiorna i dati di addestramento per scenari di fatturazione complessi",
    'ak.strategicImprovements': "Miglioramenti strategici",
    'ak.strategic1': "Implementa l'analisi del sentiment per un instradamento migliore",
    'ak.strategic2': "Aggiungi il supporto multilingue per i clienti globali",
    'ak.strategic3': "Sviluppa capacità di rilevamento proattivo dei problemi",
    'ak.teamDevelopment': "Sviluppo del team",
    'ak.team1': "Forma gli agenti sui casi inoltrati dall'IA",
    'ak.team2': "Crea cicli di feedback per il miglioramento continuo",
    'ak.team3': "Sviluppa flussi di lavoro di collaborazione tra IA e persone",
  },
  ja: {
    'ak.notAvailableYet': "まだ利用できません",
    'ak.missingInformation': "情報が不足しています",
    'ak.exportComplete': "エクスポート完了",
    'ak.cat.general': "一般",
    'ak.cat.billing': "請求",
    'ak.cat.technical': "技術",
    'ak.cat.account': "アカウント",
    'ak.cat.product': "製品",
    'ak.cat.shipping': "配送",
    'ak.trainingUnavailableDesc': "AIモデルのトレーニングは今後のリリースで利用可能になります。",
    'ak.fillRequiredFields': "すべての必須項目を入力してください",
    'ak.saveScenariosUnavailableDesc': "トレーニングシナリオの保存は今後のリリースで利用可能になります。",
    'ak.missingFeedback': "フィードバックがありません",
    'ak.provideFeedbackContent': "フィードバックの内容を入力してください",
    'ak.submitFeedbackUnavailableDesc': "AIフィードバックの送信は今後のリリースで利用可能になります。",
    'ak.loadingLearningCenter': "ラーニングセンターを読み込み中...",
    'ak.learningCenterTitle': "AIラーニングセンター",
    'ak.learningCenterSubtitle': "AIのパフォーマンスをトレーニング、改善、最適化します",
    'ak.trainingActive': "トレーニング中",
    'ak.ready': "準備完了",
    'ak.startTraining': "トレーニングを開始",
    'ak.pauseTraining': "トレーニングを一時停止",
    'ak.modelAccuracy': "モデルの精度",
    'ak.learningRate': "学習率",
    'ak.trainingProgress': "トレーニングの進捗",
    'ak.lastTraining': "前回のトレーニング",
    'ak.tab.customerFeedback': "顧客フィードバック",
    'ak.tab.trainingScenarios': "トレーニングシナリオ",
    'ak.tab.learningOpportunities': "学習の機会",
    'ak.tab.modelPerformance': "モデルのパフォーマンス",
    'ak.recentCustomerFeedback': "最近の顧客フィードバック",
    'ak.addFeedback': "フィードバックを追加",
    'ak.submitAiFeedback': "AIフィードバックを送信",
    'ak.submitAiFeedbackDesc': "最近のやり取りに関するフィードバックを提供して、AIの改善にご協力ください",
    'ak.conversationIdOptional': "会話ID（任意）",
    'ak.enterConversationId': "会話IDを入力",
    'ak.rating': "評価",
    'ak.feedbackRequired': "フィードバック *",
    'ak.feedbackPlaceholder': "AIの応答についての体験を説明してください...",
    'ak.suggestionsForImprovement': "改善の提案",
    'ak.suggestionsPlaceholder': "AIの応答をどのように改善できますか？",
    'ak.submitFeedback': "フィードバックを送信",
    'ak.noFeedbackYet': "フィードバックはまだありません",
    'ak.noFeedbackDesc': "AIとのやり取りに関する顧客フィードバックは、今後のリリースでここに表示されます。",
    'ak.needsReview': "要確認",
    'ak.conversationLabel': "会話:",
    'ak.importScenarios': "シナリオをインポート",
    'ak.importTrainingScenarios': "トレーニングシナリオをインポート",
    'ak.importScenariosDesc': "トレーニングシナリオを含むJSONまたはCSVファイルをアップロード",
    'ak.importDropHint': "ファイルをドラッグ＆ドロップするか選択して、トレーニングシナリオをインポートします。",
    'ak.addScenario': "シナリオを追加",
    'ak.createTrainingScenario': "トレーニングシナリオを作成",
    'ak.createTrainingScenarioDesc': "AIの応答を改善するための新しいトレーニングシナリオを追加します",
    'ak.scenarioTitleRequired': "シナリオのタイトル *",
    'ak.enterScenarioTitle': "シナリオのタイトルを入力",
    'ak.category': "カテゴリー",
    'ak.scenarioCatGeneral': "一般サポート",
    'ak.customerScenarioRequired': "顧客シナリオ *",
    'ak.customerScenarioPlaceholder': "顧客の状況または質問を説明してください...",
    'ak.expectedAiResponseRequired': "期待されるAIの応答 *",
    'ak.expectedAiResponsePlaceholder': "このシナリオでAIはどう応答すべきですか？",
    'ak.difficultyLevel': "難易度",
    'ak.difficulty.easy': "簡単",
    'ak.difficulty.medium': "普通",
    'ak.difficulty.hard': "難しい",
    'ak.difficulty.expert': "エキスパート",
    'ak.tags': "タグ",
    'ak.tagsCommaPlaceholder': "カンマ区切りのタグ",
    'ak.noScenariosYet': "トレーニングシナリオはまだありません",
    'ak.noScenariosDesc': "保存されたトレーニングシナリオは、今後のリリースでここに表示されます。",
    'ak.scenarioLabel': "シナリオ:",
    'ak.expectedResponseLabel': "期待される応答:",
    'ak.noOpportunitiesYet': "学習の機会はまだありません",
    'ak.noOpportunitiesDesc': "学習機会の自動検出は今後のリリースで利用可能になります。",
    'ak.prioritySuffix': "優先度",
    'ak.recommendedAction': "推奨アクション:",
    'ak.expectedImpact': "期待される効果:",
    'ak.implement': "実装",
    'ak.schedule': "スケジュール",
    'ak.modelPerformanceAnalytics': "モデルパフォーマンス分析",
    'ak.aiModelPerformance': "AIモデルのパフォーマンス",
    'ak.learningProgress': "学習の進捗",
    'ak.trainingStatus': "トレーニングの状態",
    'ak.trainingStatusFallback': "AI自動解決はホスト型LLMで実行されており、ローカルモデルのトレーニング指標は記録されません。",
    'ak.aiSolutionsPending': "レビュー待ちのAIソリューション",
    'ak.approvedIntoSolutionBank': "ソリューションバンクに承認済み",
    'ak.trainingAchievements': "トレーニングの実績",
    'ak.noAchievementsYet': "トレーニングの実績はまだありません",
    'ak.noAchievementsDesc': "モデルトレーニングが有効になると、マイルストーンがここに表示されます。",
    'ak.exportReportUnavailableDesc': "パフォーマンスレポートのエクスポートは今後のリリースで利用可能になります。",
    'ak.exportPerformanceReport': "パフォーマンスレポートをエクスポート",
    'ak.failedLoadKb': "ナレッジベースのエントリの読み込みに失敗しました",
    'ak.fillTitleContent': "タイトルと内容を入力してください",
    'ak.entryCreated': "エントリを作成しました",
    'ak.entryCreatedDesc': "ナレッジベースのエントリが正常に作成されました",
    'ak.failedCreateEntry': "ナレッジベースのエントリの作成に失敗しました",
    'ak.entryUpdated': "エントリを更新しました",
    'ak.entryUpdatedDesc': "ナレッジベースのエントリが正常に更新されました",
    'ak.failedUpdateEntry': "ナレッジベースのエントリの更新に失敗しました",
    'ak.entryDeleted': "エントリを削除しました",
    'ak.entryDeletedDesc': "ナレッジベースのエントリが正常に削除されました",
    'ak.failedDeleteEntry': "ナレッジベースのエントリの削除に失敗しました",
    'ak.nothingToExport': "エクスポートする項目がありません",
    'ak.nothingToExportDesc': "エクスポートするナレッジベースのエントリはまだありません。",
    'ak.kbExportedDesc': "ナレッジベースをJSONとしてエクスポートしました。",
    'ak.importUnavailableDesc': "インポートは今後のリリースで利用可能になります。",
    'ak.loadingKb': "ナレッジベースを読み込み中...",
    'ak.kbManagementTitle': "ナレッジベース管理",
    'ak.kbManagementSubtitle': "AIナレッジエントリとトレーニングコンテンツを管理します",
    'ak.entriesBadge': "{count}件のエントリ",
    'ak.import': "インポート",
    'ak.export': "エクスポート",
    'ak.totalEntries': "エントリ総数",
    'ak.avgSuccessRate': "平均成功率",
    'ak.totalUsage': "合計使用回数",
    'ak.categories': "カテゴリー",
    'ak.tab.browseEntries': "エントリを閲覧",
    'ak.tab.createEntry': "エントリを作成",
    'ak.tab.analytics': "分析",
    'ak.searchKbPlaceholder': "ナレッジベースを検索...",
    'ak.allCategories': "すべてのカテゴリー",
    'ak.usedTimesUpdated': "{count}回使用 · 最終更新 {date}",
    'ak.resolutionStepsLabel': "解決手順:",
    'ak.noEntriesFound': "エントリが見つかりません",
    'ak.noEntriesDesc': "検索条件を調整するか、新しいナレッジベースのエントリを作成してください",
    'ak.createKbEntry': "ナレッジベースのエントリを作成",
    'ak.entryTitleRequired': "エントリのタイトル *",
    'ak.enterEntryTitle': "エントリのタイトルを入力",
    'ak.contentRequired': "内容 *",
    'ak.enterKbContent': "ナレッジベースの内容を入力",
    'ak.tagsCommaSeparated': "タグ（カンマ区切り）",
    'ak.tagsExamplePlaceholder': "パスワード, リセット, ログイン, セキュリティ",
    'ak.resolutionStepsOnePerLine': "解決手順（1行に1つ）",
    'ak.resolutionStepsPlaceholder': "手順1: ログインページに移動\n手順2: パスワードをお忘れですかをクリック\n手順3: メールアドレスを入力",
    'ak.saveEntry': "エントリを保存",
    'ak.kbAnalytics': "ナレッジベース分析",
    'ak.performanceOverview': "パフォーマンス概要",
    'ak.overallSuccessRate': "全体の成功率",
    'ak.knowledgeCoverage': "ナレッジのカバレッジ",
    'ak.usageFrequency': "使用頻度",
    'ak.topPerformingEntries': "上位パフォーマンスのエントリ",
    'ak.viewDetailedAnalytics': "詳細な分析を表示",
    'ak.editKbEntry': "ナレッジベースのエントリを編集",
    'ak.editKbEntryDesc': "ナレッジベースのエントリの詳細を変更します",
    'ak.updateEntry': "エントリを更新",
    'ak.areYouSure': "よろしいですか？",
    'ak.deleteConfirmPrefix': "この操作は取り消せません。ナレッジベースのエントリが完全に削除されます",
    'ak.deleteConfirmSuffix': "関連するすべてのデータも削除されます。",
    'ak.deleteEntry': "エントリを削除",
    'ak.detailedKbAnalytics': "ナレッジベースの詳細分析",
    'ak.detailedKbAnalyticsDesc': "ナレッジベースのパフォーマンスとインサイトの包括的な分析",
    'ak.categoryPerformanceAnalysis': "カテゴリー別パフォーマンス分析",
    'ak.entriesLower': "{count}件のエントリ",
    'ak.successRate': "成功率",
    'ak.usage': "使用状況",
    'ak.aiPerformanceInsights': "AIパフォーマンスのインサイト",
    'ak.highPerformingContent': "高パフォーマンスのコンテンツ",
    'ak.entriesAbove90': "{count}件のエントリの成功率が90%を超えています",
    'ak.optimizationOpportunities': "最適化の機会",
    'ak.entriesBenefitOptimization': "{count}件のエントリは最適化により改善できる可能性があります",
    'ak.usageTrendsRecommendations': "使用傾向と推奨事項",
    'ak.mostUsedEntry': "最も使用されたエントリ:",
    'ak.uses': "回使用",
    'ak.bestPerformingEntry': "最高パフォーマンスのエントリ:",
    'ak.success': "成功",
    'ak.aiRecommendations': "AIの推奨事項:",
    'ak.recExpandCategories': "高パフォーマンスのカテゴリーにエントリを追加して拡充することを検討してください",
    'ak.recReviewLowSuccess': "成功率が80%未満のエントリを見直して最適化してください",
    'ak.recAddResolutionSteps': "頻繁に使用されるエントリに解決手順を追加してください",
    'ak.recMonitorFeedback': "コンテンツ改善のために顧客フィードバックを監視してください",
    'ak.failedLoadAnalytics': "AI分析データの読み込みに失敗しました",
    'ak.analyticsExportedDesc': "AI分析データが正常にエクスポートされました",
    'ak.loadingAnalytics': "AI分析を読み込み中...",
    'ak.analyticsDashboardTitle': "AI分析ダッシュボード",
    'ak.analyticsDashboardSubtitle': "包括的なAIパフォーマンス指標とインサイト",
    'ak.range.24h': "過去24時間",
    'ak.range.7d': "過去7日間",
    'ak.range.30d': "過去30日間",
    'ak.range.90d': "過去90日間",
    'ak.lastUpdated': "最終更新: {time}",
    'ak.totalConversations': "会話総数",
    'ak.aiHandled': "AIが対応",
    'ak.autoResolutionRate': "自動解決率",
    'ak.currentPeriod': "現在の期間",
    'ak.avgResponseTime': "平均応答時間",
    'ak.customerSatisfaction': "顧客満足度",
    'ak.tab.categoryAnalysis': "カテゴリー分析",
    'ak.tab.trendsPatterns': "傾向とパターン",
    'ak.tab.aiInsights': "AIインサイト",
    'ak.resolutionPerformance': "解決パフォーマンス",
    'ak.successfulResolutions': "成功した解決",
    'ak.escalationRate': "エスカレーション率",
    'ak.aiConfidenceScore': "AI信頼スコア",
    'ak.hourlyPerformance': "時間別パフォーマンス",
    'ak.conversationsCount': "{count}件の会話",
    'ak.performanceByCategory': "カテゴリー別パフォーマンス",
    'ak.performanceTrends': "パフォーマンスの傾向",
    'ak.trendComparisonsUnavailable': "傾向の比較はまだ利用できません",
    'ak.trendComparisonsDesc': "期間ごとの傾向は今後のリリースで利用可能になります。",
    'ak.usagePatterns': "使用パターン",
    'ak.peakHours': "ピーク時間",
    'ak.mostCommonIssues': "最も一般的な問題",
    'ak.resolutionTime': "解決時間",
    'ak.average': "平均",
    'ak.aiInsightsUnavailable': "AIパフォーマンスのインサイトはまだ利用できません",
    'ak.aiInsightsDesc': "インサイトの自動分析は今後のリリースで利用可能になります。",
    'ak.bestPracticeGuidance': "ベストプラクティスのガイダンス",
    'ak.bestPracticeNote': "AIパフォーマンスを向上させるための一般的なガイダンスです — 実際のデータから導き出されたものではありません。",
    'ak.immediateActions': "即時のアクション",
    'ak.immediate1': "技術サポートのナレッジベースエントリを追加する",
    'ak.immediate2': "パターン分析のためにエスカレーションされた会話を確認する",
    'ak.immediate3': "複雑な請求シナリオのトレーニングデータを更新する",
    'ak.strategicImprovements': "戦略的改善",
    'ak.strategic1': "より適切なルーティングのために感情分析を実装する",
    'ak.strategic2': "グローバルな顧客向けに多言語サポートを追加する",
    'ak.strategic3': "積極的な問題検出機能を開発する",
    'ak.teamDevelopment': "チームの育成",
    'ak.team1': "AIがエスカレーションしたケースについてエージェントをトレーニングする",
    'ak.team2': "継続的な改善のためのフィードバックループを作成する",
    'ak.team3': "AIと人間の協働ワークフローを開発する",
  },
  ko: {
    'ak.notAvailableYet': "아직 사용할 수 없음",
    'ak.missingInformation': "정보 누락",
    'ak.exportComplete': "내보내기 완료",
    'ak.cat.general': "일반",
    'ak.cat.billing': "청구",
    'ak.cat.technical': "기술",
    'ak.cat.account': "계정",
    'ak.cat.product': "제품",
    'ak.cat.shipping': "배송",
    'ak.trainingUnavailableDesc': "AI 모델 학습은 향후 릴리스에서 제공될 예정입니다.",
    'ak.fillRequiredFields': "모든 필수 항목을 입력하세요",
    'ak.saveScenariosUnavailableDesc': "학습 시나리오 저장은 향후 릴리스에서 제공될 예정입니다.",
    'ak.missingFeedback': "피드백 누락",
    'ak.provideFeedbackContent': "피드백 내용을 입력하세요",
    'ak.submitFeedbackUnavailableDesc': "AI 피드백 제출은 향후 릴리스에서 제공될 예정입니다.",
    'ak.loadingLearningCenter': "학습 센터 로딩 중...",
    'ak.learningCenterTitle': "AI 학습 센터",
    'ak.learningCenterSubtitle': "AI 성능을 학습, 개선 및 최적화하세요",
    'ak.trainingActive': "학습 진행 중",
    'ak.ready': "준비 완료",
    'ak.startTraining': "학습 시작",
    'ak.pauseTraining': "학습 일시중지",
    'ak.modelAccuracy': "모델 정확도",
    'ak.learningRate': "학습률",
    'ak.trainingProgress': "학습 진행률",
    'ak.lastTraining': "마지막 학습",
    'ak.tab.customerFeedback': "고객 피드백",
    'ak.tab.trainingScenarios': "학습 시나리오",
    'ak.tab.learningOpportunities': "학습 기회",
    'ak.tab.modelPerformance': "모델 성능",
    'ak.recentCustomerFeedback': "최근 고객 피드백",
    'ak.addFeedback': "피드백 추가",
    'ak.submitAiFeedback': "AI 피드백 제출",
    'ak.submitAiFeedbackDesc': "최근 상호작용에 대한 피드백을 제공하여 AI 개선에 도움을 주세요",
    'ak.conversationIdOptional': "대화 ID (선택 사항)",
    'ak.enterConversationId': "대화 ID 입력",
    'ak.rating': "평점",
    'ak.feedbackRequired': "피드백 *",
    'ak.feedbackPlaceholder': "AI 응답에 대한 경험을 설명하세요...",
    'ak.suggestionsForImprovement': "개선 제안",
    'ak.suggestionsPlaceholder': "AI 응답을 어떻게 개선할 수 있을까요?",
    'ak.submitFeedback': "피드백 제출",
    'ak.noFeedbackYet': "아직 피드백이 없습니다",
    'ak.noFeedbackDesc': "AI 상호작용에 대한 고객 피드백은 향후 릴리스에서 여기에 표시됩니다.",
    'ak.needsReview': "검토 필요",
    'ak.conversationLabel': "대화:",
    'ak.importScenarios': "시나리오 가져오기",
    'ak.importTrainingScenarios': "학습 시나리오 가져오기",
    'ak.importScenariosDesc': "학습 시나리오가 포함된 JSON 또는 CSV 파일을 업로드하세요",
    'ak.importDropHint': "파일을 끌어다 놓거나 선택하여 학습 시나리오를 가져오세요.",
    'ak.addScenario': "시나리오 추가",
    'ak.createTrainingScenario': "학습 시나리오 만들기",
    'ak.createTrainingScenarioDesc': "AI 응답을 개선하기 위한 새 학습 시나리오를 추가하세요",
    'ak.scenarioTitleRequired': "시나리오 제목 *",
    'ak.enterScenarioTitle': "시나리오 제목 입력",
    'ak.category': "카테고리",
    'ak.scenarioCatGeneral': "일반 지원",
    'ak.customerScenarioRequired': "고객 시나리오 *",
    'ak.customerScenarioPlaceholder': "고객의 상황이나 질문을 설명하세요...",
    'ak.expectedAiResponseRequired': "예상 AI 응답 *",
    'ak.expectedAiResponsePlaceholder': "이 시나리오에서 AI는 어떻게 응답해야 하나요?",
    'ak.difficultyLevel': "난이도",
    'ak.difficulty.easy': "쉬움",
    'ak.difficulty.medium': "보통",
    'ak.difficulty.hard': "어려움",
    'ak.difficulty.expert': "전문가",
    'ak.tags': "태그",
    'ak.tagsCommaPlaceholder': "쉼표로 구분된 태그",
    'ak.noScenariosYet': "아직 학습 시나리오가 없습니다",
    'ak.noScenariosDesc': "저장된 학습 시나리오는 향후 릴리스에서 여기에 표시됩니다.",
    'ak.scenarioLabel': "시나리오:",
    'ak.expectedResponseLabel': "예상 응답:",
    'ak.noOpportunitiesYet': "아직 학습 기회가 없습니다",
    'ak.noOpportunitiesDesc': "학습 기회 자동 감지는 향후 릴리스에서 제공될 예정입니다.",
    'ak.prioritySuffix': "우선순위",
    'ak.recommendedAction': "권장 조치:",
    'ak.expectedImpact': "예상 효과:",
    'ak.implement': "구현",
    'ak.schedule': "예약",
    'ak.modelPerformanceAnalytics': "모델 성능 분석",
    'ak.aiModelPerformance': "AI 모델 성능",
    'ak.learningProgress': "학습 진행률",
    'ak.trainingStatus': "학습 상태",
    'ak.trainingStatusFallback': "AI 자동 해결은 호스팅된 LLM에서 실행되며, 로컬 모델 학습 지표는 기록되지 않습니다.",
    'ak.aiSolutionsPending': "검토 대기 중인 AI 솔루션",
    'ak.approvedIntoSolutionBank': "솔루션 뱅크에 승인됨",
    'ak.trainingAchievements': "학습 성과",
    'ak.noAchievementsYet': "아직 학습 성과가 없습니다",
    'ak.noAchievementsDesc': "모델 학습이 활성화되면 마일스톤이 여기에 표시됩니다.",
    'ak.exportReportUnavailableDesc': "성능 보고서 내보내기는 향후 릴리스에서 제공될 예정입니다.",
    'ak.exportPerformanceReport': "성능 보고서 내보내기",
    'ak.failedLoadKb': "지식 베이스 항목을 불러오지 못했습니다",
    'ak.fillTitleContent': "제목과 내용을 입력하세요",
    'ak.entryCreated': "항목 생성됨",
    'ak.entryCreatedDesc': "지식 베이스 항목이 성공적으로 생성되었습니다",
    'ak.failedCreateEntry': "지식 베이스 항목을 생성하지 못했습니다",
    'ak.entryUpdated': "항목 업데이트됨",
    'ak.entryUpdatedDesc': "지식 베이스 항목이 성공적으로 업데이트되었습니다",
    'ak.failedUpdateEntry': "지식 베이스 항목을 업데이트하지 못했습니다",
    'ak.entryDeleted': "항목 삭제됨",
    'ak.entryDeletedDesc': "지식 베이스 항목이 성공적으로 삭제되었습니다",
    'ak.failedDeleteEntry': "지식 베이스 항목을 삭제하지 못했습니다",
    'ak.nothingToExport': "내보낼 항목이 없습니다",
    'ak.nothingToExportDesc': "아직 내보낼 지식 베이스 항목이 없습니다.",
    'ak.kbExportedDesc': "지식 베이스를 JSON으로 내보냈습니다.",
    'ak.importUnavailableDesc': "가져오기는 향후 릴리스에서 제공될 예정입니다.",
    'ak.loadingKb': "지식 베이스 로딩 중...",
    'ak.kbManagementTitle': "지식 베이스 관리",
    'ak.kbManagementSubtitle': "AI 지식 항목과 학습 콘텐츠를 관리하세요",
    'ak.entriesBadge': "{count}개 항목",
    'ak.import': "가져오기",
    'ak.export': "내보내기",
    'ak.totalEntries': "전체 항목",
    'ak.avgSuccessRate': "평균 성공률",
    'ak.totalUsage': "총 사용량",
    'ak.categories': "카테고리",
    'ak.tab.browseEntries': "항목 찾아보기",
    'ak.tab.createEntry': "항목 만들기",
    'ak.tab.analytics': "분석",
    'ak.searchKbPlaceholder': "지식 베이스 검색...",
    'ak.allCategories': "모든 카테고리",
    'ak.usedTimesUpdated': "{count}회 사용 · 마지막 업데이트 {date}",
    'ak.resolutionStepsLabel': "해결 단계:",
    'ak.noEntriesFound': "항목을 찾을 수 없습니다",
    'ak.noEntriesDesc': "검색 조건을 조정하거나 새 지식 베이스 항목을 만들어 보세요",
    'ak.createKbEntry': "지식 베이스 항목 만들기",
    'ak.entryTitleRequired': "항목 제목 *",
    'ak.enterEntryTitle': "항목 제목 입력",
    'ak.contentRequired': "내용 *",
    'ak.enterKbContent': "지식 베이스 내용 입력",
    'ak.tagsCommaSeparated': "태그 (쉼표로 구분)",
    'ak.tagsExamplePlaceholder': "비밀번호, 재설정, 로그인, 보안",
    'ak.resolutionStepsOnePerLine': "해결 단계 (한 줄에 하나씩)",
    'ak.resolutionStepsPlaceholder': "1단계: 로그인 페이지로 이동\n2단계: 비밀번호 찾기 클릭\n3단계: 이메일 주소 입력",
    'ak.saveEntry': "항목 저장",
    'ak.kbAnalytics': "지식 베이스 분석",
    'ak.performanceOverview': "성능 개요",
    'ak.overallSuccessRate': "전체 성공률",
    'ak.knowledgeCoverage': "지식 범위",
    'ak.usageFrequency': "사용 빈도",
    'ak.topPerformingEntries': "성능이 가장 좋은 항목",
    'ak.viewDetailedAnalytics': "상세 분석 보기",
    'ak.editKbEntry': "지식 베이스 항목 편집",
    'ak.editKbEntryDesc': "지식 베이스 항목 세부 정보를 수정하세요",
    'ak.updateEntry': "항목 업데이트",
    'ak.areYouSure': "확실합니까?",
    'ak.deleteConfirmPrefix': "이 작업은 취소할 수 없습니다. 지식 베이스 항목이 영구적으로 삭제됩니다",
    'ak.deleteConfirmSuffix': "관련된 모든 데이터도 제거됩니다.",
    'ak.deleteEntry': "항목 삭제",
    'ak.detailedKbAnalytics': "상세 지식 베이스 분석",
    'ak.detailedKbAnalyticsDesc': "지식 베이스 성능 및 인사이트에 대한 종합 분석",
    'ak.categoryPerformanceAnalysis': "카테고리별 성능 분석",
    'ak.entriesLower': "{count}개 항목",
    'ak.successRate': "성공률",
    'ak.usage': "사용량",
    'ak.aiPerformanceInsights': "AI 성능 인사이트",
    'ak.highPerformingContent': "고성능 콘텐츠",
    'ak.entriesAbove90': "{count}개 항목의 성공률이 90%를 초과합니다",
    'ak.optimizationOpportunities': "최적화 기회",
    'ak.entriesBenefitOptimization': "{count}개 항목이 최적화로 개선될 수 있습니다",
    'ak.usageTrendsRecommendations': "사용 추세 및 권장 사항",
    'ak.mostUsedEntry': "가장 많이 사용된 항목:",
    'ak.uses': "회 사용",
    'ak.bestPerformingEntry': "성능이 가장 좋은 항목:",
    'ak.success': "성공",
    'ak.aiRecommendations': "AI 권장 사항:",
    'ak.recExpandCategories': "성능이 좋은 카테고리에 항목을 더 추가하여 확장하는 것을 고려하세요",
    'ak.recReviewLowSuccess': "성공률이 80% 미만인 항목을 검토하고 최적화하세요",
    'ak.recAddResolutionSteps': "자주 사용되는 항목에 해결 단계를 더 추가하세요",
    'ak.recMonitorFeedback': "콘텐츠 개선을 위해 고객 피드백을 모니터링하세요",
    'ak.failedLoadAnalytics': "AI 분석 데이터를 불러오지 못했습니다",
    'ak.analyticsExportedDesc': "AI 분석 데이터가 성공적으로 내보내졌습니다",
    'ak.loadingAnalytics': "AI 분석 로딩 중...",
    'ak.analyticsDashboardTitle': "AI 분석 대시보드",
    'ak.analyticsDashboardSubtitle': "종합적인 AI 성능 지표 및 인사이트",
    'ak.range.24h': "지난 24시간",
    'ak.range.7d': "지난 7일",
    'ak.range.30d': "지난 30일",
    'ak.range.90d': "지난 90일",
    'ak.lastUpdated': "마지막 업데이트: {time}",
    'ak.totalConversations': "전체 대화",
    'ak.aiHandled': "AI 처리",
    'ak.autoResolutionRate': "자동 해결률",
    'ak.currentPeriod': "현재 기간",
    'ak.avgResponseTime': "평균 응답 시간",
    'ak.customerSatisfaction': "고객 만족도",
    'ak.tab.categoryAnalysis': "카테고리 분석",
    'ak.tab.trendsPatterns': "추세 및 패턴",
    'ak.tab.aiInsights': "AI 인사이트",
    'ak.resolutionPerformance': "해결 성능",
    'ak.successfulResolutions': "성공한 해결",
    'ak.escalationRate': "에스컬레이션 비율",
    'ak.aiConfidenceScore': "AI 신뢰도 점수",
    'ak.hourlyPerformance': "시간별 성능",
    'ak.conversationsCount': "{count}건의 대화",
    'ak.performanceByCategory': "카테고리별 성능",
    'ak.performanceTrends': "성능 추세",
    'ak.trendComparisonsUnavailable': "추세 비교는 아직 사용할 수 없습니다",
    'ak.trendComparisonsDesc': "기간별 추세는 향후 릴리스에서 제공될 예정입니다.",
    'ak.usagePatterns': "사용 패턴",
    'ak.peakHours': "피크 시간",
    'ak.mostCommonIssues': "가장 일반적인 문제",
    'ak.resolutionTime': "해결 시간",
    'ak.average': "평균",
    'ak.aiInsightsUnavailable': "AI 성능 인사이트는 아직 사용할 수 없습니다",
    'ak.aiInsightsDesc': "인사이트 자동 분석은 향후 릴리스에서 제공될 예정입니다.",
    'ak.bestPracticeGuidance': "모범 사례 가이드",
    'ak.bestPracticeNote': "AI 성능 향상을 위한 일반적인 가이드입니다 — 실시간 데이터에서 도출된 것이 아닙니다.",
    'ak.immediateActions': "즉각적인 조치",
    'ak.immediate1': "기술 지원 지식 베이스 항목을 더 추가하세요",
    'ak.immediate2': "패턴 분석을 위해 에스컬레이션된 대화를 검토하세요",
    'ak.immediate3': "복잡한 청구 시나리오에 대한 학습 데이터를 업데이트하세요",
    'ak.strategicImprovements': "전략적 개선",
    'ak.strategic1': "더 나은 라우팅을 위해 감정 분석을 구현하세요",
    'ak.strategic2': "글로벌 고객을 위한 다국어 지원을 추가하세요",
    'ak.strategic3': "선제적 문제 감지 기능을 개발하세요",
    'ak.teamDevelopment': "팀 개발",
    'ak.team1': "AI가 에스컬레이션한 사례에 대해 상담원을 교육하세요",
    'ak.team2': "지속적인 개선을 위한 피드백 루프를 만드세요",
    'ak.team3': "AI와 인간의 협업 워크플로를 개발하세요",
  },
  zh: {
    'ak.notAvailableYet': "尚不可用",
    'ak.missingInformation': "信息缺失",
    'ak.exportComplete': "导出完成",
    'ak.cat.general': "常规",
    'ak.cat.billing': "账单",
    'ak.cat.technical': "技术",
    'ak.cat.account': "账户",
    'ak.cat.product': "产品",
    'ak.cat.shipping': "配送",
    'ak.trainingUnavailableDesc': "AI 模型训练将在即将发布的版本中提供。",
    'ak.fillRequiredFields': "请填写所有必填字段",
    'ak.saveScenariosUnavailableDesc': "保存训练场景将在即将发布的版本中提供。",
    'ak.missingFeedback': "反馈缺失",
    'ak.provideFeedbackContent': "请提供反馈内容",
    'ak.submitFeedbackUnavailableDesc': "提交 AI 反馈将在即将发布的版本中提供。",
    'ak.loadingLearningCenter': "正在加载学习中心...",
    'ak.learningCenterTitle': "AI 学习中心",
    'ak.learningCenterSubtitle': "训练、改进并优化 AI 性能",
    'ak.trainingActive': "训练进行中",
    'ak.ready': "就绪",
    'ak.startTraining': "开始训练",
    'ak.pauseTraining': "暂停训练",
    'ak.modelAccuracy': "模型准确率",
    'ak.learningRate': "学习率",
    'ak.trainingProgress': "训练进度",
    'ak.lastTraining': "上次训练",
    'ak.tab.customerFeedback': "客户反馈",
    'ak.tab.trainingScenarios': "训练场景",
    'ak.tab.learningOpportunities': "学习机会",
    'ak.tab.modelPerformance': "模型性能",
    'ak.recentCustomerFeedback': "最近的客户反馈",
    'ak.addFeedback': "添加反馈",
    'ak.submitAiFeedback': "提交 AI 反馈",
    'ak.submitAiFeedbackDesc': "通过提供近期互动的反馈，帮助我们改进 AI",
    'ak.conversationIdOptional': "对话 ID（可选）",
    'ak.enterConversationId': "输入对话 ID",
    'ak.rating': "评分",
    'ak.feedbackRequired': "反馈 *",
    'ak.feedbackPlaceholder': "描述您对 AI 回复的体验...",
    'ak.suggestionsForImprovement': "改进建议",
    'ak.suggestionsPlaceholder': "AI 回复可以如何改进？",
    'ak.submitFeedback': "提交反馈",
    'ak.noFeedbackYet': "暂无反馈",
    'ak.noFeedbackDesc': "关于 AI 互动的客户反馈将在即将发布的版本中显示在此处。",
    'ak.needsReview': "需要审核",
    'ak.conversationLabel': "对话：",
    'ak.importScenarios': "导入场景",
    'ak.importTrainingScenarios': "导入训练场景",
    'ak.importScenariosDesc': "上传包含训练场景的 JSON 或 CSV 文件",
    'ak.importDropHint': "拖放或选择文件以导入训练场景。",
    'ak.addScenario': "添加场景",
    'ak.createTrainingScenario': "创建训练场景",
    'ak.createTrainingScenarioDesc': "添加新的训练场景以改进 AI 回复",
    'ak.scenarioTitleRequired': "场景标题 *",
    'ak.enterScenarioTitle': "输入场景标题",
    'ak.category': "类别",
    'ak.scenarioCatGeneral': "常规支持",
    'ak.customerScenarioRequired': "客户场景 *",
    'ak.customerScenarioPlaceholder': "描述客户的情况或问题...",
    'ak.expectedAiResponseRequired': "预期的 AI 回复 *",
    'ak.expectedAiResponsePlaceholder': "在此场景中 AI 应如何回复？",
    'ak.difficultyLevel': "难度级别",
    'ak.difficulty.easy': "简单",
    'ak.difficulty.medium': "中等",
    'ak.difficulty.hard': "困难",
    'ak.difficulty.expert': "专家",
    'ak.tags': "标签",
    'ak.tagsCommaPlaceholder': "以逗号分隔的标签",
    'ak.noScenariosYet': "暂无训练场景",
    'ak.noScenariosDesc': "已保存的训练场景将在即将发布的版本中显示在此处。",
    'ak.scenarioLabel': "场景：",
    'ak.expectedResponseLabel': "预期回复：",
    'ak.noOpportunitiesYet': "暂无学习机会",
    'ak.noOpportunitiesDesc': "学习机会的自动检测将在即将发布的版本中提供。",
    'ak.prioritySuffix': "优先级",
    'ak.recommendedAction': "建议操作：",
    'ak.expectedImpact': "预期影响：",
    'ak.implement': "实施",
    'ak.schedule': "计划",
    'ak.modelPerformanceAnalytics': "模型性能分析",
    'ak.aiModelPerformance': "AI 模型性能",
    'ak.learningProgress': "学习进度",
    'ak.trainingStatus': "训练状态",
    'ak.trainingStatusFallback': "AI 自动解决在托管的 LLM 上运行；不会记录本地模型训练指标。",
    'ak.aiSolutionsPending': "待审核的 AI 解决方案",
    'ak.approvedIntoSolutionBank': "已批准进入解决方案库",
    'ak.trainingAchievements': "训练成就",
    'ak.noAchievementsYet': "暂无训练成就",
    'ak.noAchievementsDesc': "一旦模型训练上线，里程碑将显示在此处。",
    'ak.exportReportUnavailableDesc': "性能报告导出将在即将发布的版本中提供。",
    'ak.exportPerformanceReport': "导出性能报告",
    'ak.failedLoadKb': "加载知识库条目失败",
    'ak.fillTitleContent': "请填写标题和内容",
    'ak.entryCreated': "条目已创建",
    'ak.entryCreatedDesc': "知识库条目已成功创建",
    'ak.failedCreateEntry': "创建知识库条目失败",
    'ak.entryUpdated': "条目已更新",
    'ak.entryUpdatedDesc': "知识库条目已成功更新",
    'ak.failedUpdateEntry': "更新知识库条目失败",
    'ak.entryDeleted': "条目已删除",
    'ak.entryDeletedDesc': "知识库条目已成功删除",
    'ak.failedDeleteEntry': "删除知识库条目失败",
    'ak.nothingToExport': "没有可导出的内容",
    'ak.nothingToExportDesc': "尚无可导出的知识库条目。",
    'ak.kbExportedDesc': "知识库已导出为 JSON。",
    'ak.importUnavailableDesc': "导入将在即将发布的版本中提供。",
    'ak.loadingKb': "正在加载知识库...",
    'ak.kbManagementTitle': "知识库管理",
    'ak.kbManagementSubtitle': "管理 AI 知识条目和训练内容",
    'ak.entriesBadge': "{count} 个条目",
    'ak.import': "导入",
    'ak.export': "导出",
    'ak.totalEntries': "条目总数",
    'ak.avgSuccessRate': "平均成功率",
    'ak.totalUsage': "总使用量",
    'ak.categories': "类别",
    'ak.tab.browseEntries': "浏览条目",
    'ak.tab.createEntry': "创建条目",
    'ak.tab.analytics': "分析",
    'ak.searchKbPlaceholder': "搜索知识库...",
    'ak.allCategories': "所有类别",
    'ak.usedTimesUpdated': "已使用 {count} 次 · 最后更新 {date}",
    'ak.resolutionStepsLabel': "解决步骤：",
    'ak.noEntriesFound': "未找到条目",
    'ak.noEntriesDesc': "尝试调整搜索条件或创建新的知识库条目",
    'ak.createKbEntry': "创建知识库条目",
    'ak.entryTitleRequired': "条目标题 *",
    'ak.enterEntryTitle': "输入条目标题",
    'ak.contentRequired': "内容 *",
    'ak.enterKbContent': "输入知识库内容",
    'ak.tagsCommaSeparated': "标签（以逗号分隔）",
    'ak.tagsExamplePlaceholder': "密码, 重置, 登录, 安全",
    'ak.resolutionStepsOnePerLine': "解决步骤（每行一个）",
    'ak.resolutionStepsPlaceholder': "步骤 1：转到登录页面\n步骤 2：点击忘记密码\n步骤 3：输入电子邮件地址",
    'ak.saveEntry': "保存条目",
    'ak.kbAnalytics': "知识库分析",
    'ak.performanceOverview': "性能概览",
    'ak.overallSuccessRate': "总体成功率",
    'ak.knowledgeCoverage': "知识覆盖率",
    'ak.usageFrequency': "使用频率",
    'ak.topPerformingEntries': "表现最佳的条目",
    'ak.viewDetailedAnalytics': "查看详细分析",
    'ak.editKbEntry': "编辑知识库条目",
    'ak.editKbEntryDesc': "修改知识库条目详情",
    'ak.updateEntry': "更新条目",
    'ak.areYouSure': "确定吗？",
    'ak.deleteConfirmPrefix': "此操作无法撤销。这将永久删除知识库条目",
    'ak.deleteConfirmSuffix': "并移除所有相关数据。",
    'ak.deleteEntry': "删除条目",
    'ak.detailedKbAnalytics': "详细知识库分析",
    'ak.detailedKbAnalyticsDesc': "对知识库性能和洞察的全面分析",
    'ak.categoryPerformanceAnalysis': "按类别的性能分析",
    'ak.entriesLower': "{count} 个条目",
    'ak.successRate': "成功率",
    'ak.usage': "使用量",
    'ak.aiPerformanceInsights': "AI 性能洞察",
    'ak.highPerformingContent': "高性能内容",
    'ak.entriesAbove90': "{count} 个条目的成功率超过 90%",
    'ak.optimizationOpportunities': "优化机会",
    'ak.entriesBenefitOptimization': "{count} 个条目可以从优化中受益",
    'ak.usageTrendsRecommendations': "使用趋势和建议",
    'ak.mostUsedEntry': "使用最多的条目：",
    'ak.uses': "次使用",
    'ak.bestPerformingEntry': "表现最佳的条目：",
    'ak.success': "成功",
    'ak.aiRecommendations': "AI 建议：",
    'ak.recExpandCategories': "考虑通过增加更多条目来扩展高性能类别",
    'ak.recReviewLowSuccess': "审核并优化成功率低于 80% 的条目",
    'ak.recAddResolutionSteps': "为常用条目添加更多解决步骤",
    'ak.recMonitorFeedback': "监控客户反馈以改进内容",
    'ak.failedLoadAnalytics': "加载 AI 分析数据失败",
    'ak.analyticsExportedDesc': "AI 分析数据已成功导出",
    'ak.loadingAnalytics': "正在加载 AI 分析...",
    'ak.analyticsDashboardTitle': "AI 分析仪表板",
    'ak.analyticsDashboardSubtitle': "全面的 AI 性能指标和洞察",
    'ak.range.24h': "最近 24 小时",
    'ak.range.7d': "最近 7 天",
    'ak.range.30d': "最近 30 天",
    'ak.range.90d': "最近 90 天",
    'ak.lastUpdated': "最后更新：{time}",
    'ak.totalConversations': "对话总数",
    'ak.aiHandled': "AI 处理",
    'ak.autoResolutionRate': "自动解决率",
    'ak.currentPeriod': "当前时段",
    'ak.avgResponseTime': "平均响应时间",
    'ak.customerSatisfaction': "客户满意度",
    'ak.tab.categoryAnalysis': "类别分析",
    'ak.tab.trendsPatterns': "趋势和模式",
    'ak.tab.aiInsights': "AI 洞察",
    'ak.resolutionPerformance': "解决性能",
    'ak.successfulResolutions': "成功解决",
    'ak.escalationRate': "升级率",
    'ak.aiConfidenceScore': "AI 置信度分数",
    'ak.hourlyPerformance': "每小时性能",
    'ak.conversationsCount': "{count} 次对话",
    'ak.performanceByCategory': "按类别的性能",
    'ak.performanceTrends': "性能趋势",
    'ak.trendComparisonsUnavailable': "趋势比较尚不可用",
    'ak.trendComparisonsDesc': "环比趋势将在即将发布的版本中提供。",
    'ak.usagePatterns': "使用模式",
    'ak.peakHours': "高峰时段",
    'ak.mostCommonIssues': "最常见的问题",
    'ak.resolutionTime': "解决时间",
    'ak.average': "平均",
    'ak.aiInsightsUnavailable': "AI 性能洞察尚不可用",
    'ak.aiInsightsDesc': "洞察的自动分析将在即将发布的版本中提供。",
    'ak.bestPracticeGuidance': "最佳实践指南",
    'ak.bestPracticeNote': "改进 AI 性能的一般性指导——并非来自您的实时数据。",
    'ak.immediateActions': "即时行动",
    'ak.immediate1': "向知识库添加更多技术支持条目",
    'ak.immediate2': "审查升级的对话以进行模式分析",
    'ak.immediate3': "为复杂的账单场景更新训练数据",
    'ak.strategicImprovements': "战略性改进",
    'ak.strategic1': "实施情感分析以实现更好的路由",
    'ak.strategic2': "为全球客户添加多语言支持",
    'ak.strategic3': "开发主动问题检测能力",
    'ak.teamDevelopment': "团队发展",
    'ak.team1': "针对 AI 升级的案例培训客服",
    'ak.team2': "创建反馈循环以持续改进",
    'ak.team3': "开发 AI 与人工协作的工作流程",
  },
  ar: {
    'ak.notAvailableYet': "غير متاح بعد",
    'ak.missingInformation': "معلومات ناقصة",
    'ak.exportComplete': "اكتمل التصدير",
    'ak.cat.general': "عام",
    'ak.cat.billing': "الفوترة",
    'ak.cat.technical': "تقني",
    'ak.cat.account': "الحساب",
    'ak.cat.product': "المنتج",
    'ak.cat.shipping': "الشحن",
    'ak.trainingUnavailableDesc': "سيكون تدريب نموذج الذكاء الاصطناعي متاحًا في إصدار قادم.",
    'ak.fillRequiredFields': "يرجى ملء جميع الحقول المطلوبة",
    'ak.saveScenariosUnavailableDesc': "سيكون حفظ سيناريوهات التدريب متاحًا في إصدار قادم.",
    'ak.missingFeedback': "التعليق ناقص",
    'ak.provideFeedbackContent': "يرجى تقديم محتوى التعليق",
    'ak.submitFeedbackUnavailableDesc': "سيكون إرسال تعليقات الذكاء الاصطناعي متاحًا في إصدار قادم.",
    'ak.loadingLearningCenter': "جارٍ تحميل مركز التعلم...",
    'ak.learningCenterTitle': "مركز التعلم بالذكاء الاصطناعي",
    'ak.learningCenterSubtitle': "درّب وحسّن وحسّن أداء الذكاء الاصطناعي",
    'ak.trainingActive': "التدريب نشط",
    'ak.ready': "جاهز",
    'ak.startTraining': "بدء التدريب",
    'ak.pauseTraining': "إيقاف التدريب مؤقتًا",
    'ak.modelAccuracy': "دقة النموذج",
    'ak.learningRate': "معدل التعلم",
    'ak.trainingProgress': "تقدم التدريب",
    'ak.lastTraining': "آخر تدريب",
    'ak.tab.customerFeedback': "تعليقات العملاء",
    'ak.tab.trainingScenarios': "سيناريوهات التدريب",
    'ak.tab.learningOpportunities': "فرص التعلم",
    'ak.tab.modelPerformance': "أداء النموذج",
    'ak.recentCustomerFeedback': "تعليقات العملاء الأخيرة",
    'ak.addFeedback': "إضافة تعليق",
    'ak.submitAiFeedback': "إرسال تعليق للذكاء الاصطناعي",
    'ak.submitAiFeedbackDesc': "ساعد في تحسين الذكاء الاصطناعي لدينا من خلال تقديم تعليقات على التفاعلات الأخيرة",
    'ak.conversationIdOptional': "معرّف المحادثة (اختياري)",
    'ak.enterConversationId': "أدخل معرّف المحادثة",
    'ak.rating': "التقييم",
    'ak.feedbackRequired': "التعليق *",
    'ak.feedbackPlaceholder': "صف تجربتك مع رد الذكاء الاصطناعي...",
    'ak.suggestionsForImprovement': "اقتراحات للتحسين",
    'ak.suggestionsPlaceholder': "كيف يمكن تحسين رد الذكاء الاصطناعي؟",
    'ak.submitFeedback': "إرسال التعليق",
    'ak.noFeedbackYet': "لا توجد تعليقات بعد",
    'ak.noFeedbackDesc': "ستظهر تعليقات العملاء على تفاعلات الذكاء الاصطناعي هنا في إصدار قادم.",
    'ak.needsReview': "بحاجة إلى مراجعة",
    'ak.conversationLabel': "المحادثة:",
    'ak.importScenarios': "استيراد السيناريوهات",
    'ak.importTrainingScenarios': "استيراد سيناريوهات التدريب",
    'ak.importScenariosDesc': "قم بتحميل ملف JSON أو CSV يحتوي على سيناريوهات التدريب",
    'ak.importDropHint': "اسحب وأفلت أو حدد ملفًا لاستيراد سيناريوهات التدريب.",
    'ak.addScenario': "إضافة سيناريو",
    'ak.createTrainingScenario': "إنشاء سيناريو تدريب",
    'ak.createTrainingScenarioDesc': "أضف سيناريو تدريب جديدًا لتحسين ردود الذكاء الاصطناعي",
    'ak.scenarioTitleRequired': "عنوان السيناريو *",
    'ak.enterScenarioTitle': "أدخل عنوان السيناريو",
    'ak.category': "الفئة",
    'ak.scenarioCatGeneral': "الدعم العام",
    'ak.customerScenarioRequired': "سيناريو العميل *",
    'ak.customerScenarioPlaceholder': "صف حالة العميل أو سؤاله...",
    'ak.expectedAiResponseRequired': "رد الذكاء الاصطناعي المتوقع *",
    'ak.expectedAiResponsePlaceholder': "بماذا يجب أن يرد الذكاء الاصطناعي في هذا السيناريو؟",
    'ak.difficultyLevel': "مستوى الصعوبة",
    'ak.difficulty.easy': "سهل",
    'ak.difficulty.medium': "متوسط",
    'ak.difficulty.hard': "صعب",
    'ak.difficulty.expert': "خبير",
    'ak.tags': "الوسوم",
    'ak.tagsCommaPlaceholder': "وسوم مفصولة بفواصل",
    'ak.noScenariosYet': "لا توجد سيناريوهات تدريب بعد",
    'ak.noScenariosDesc': "ستظهر سيناريوهات التدريب المحفوظة هنا في إصدار قادم.",
    'ak.scenarioLabel': "السيناريو:",
    'ak.expectedResponseLabel': "الرد المتوقع:",
    'ak.noOpportunitiesYet': "لا توجد فرص تعلم بعد",
    'ak.noOpportunitiesDesc': "سيكون الكشف التلقائي عن فرص التعلم متاحًا في إصدار قادم.",
    'ak.prioritySuffix': "الأولوية",
    'ak.recommendedAction': "الإجراء الموصى به:",
    'ak.expectedImpact': "الأثر المتوقع:",
    'ak.implement': "تنفيذ",
    'ak.schedule': "جدولة",
    'ak.modelPerformanceAnalytics': "تحليلات أداء النموذج",
    'ak.aiModelPerformance': "أداء نموذج الذكاء الاصطناعي",
    'ak.learningProgress': "تقدم التعلم",
    'ak.trainingStatus': "حالة التدريب",
    'ak.trainingStatusFallback': "يعمل الحل التلقائي بالذكاء الاصطناعي على نموذج لغوي كبير مستضاف؛ لا يتم تسجيل مقاييس تدريب نموذج محلي.",
    'ak.aiSolutionsPending': "حلول الذكاء الاصطناعي قيد المراجعة",
    'ak.approvedIntoSolutionBank': "معتمدة في بنك الحلول",
    'ak.trainingAchievements': "إنجازات التدريب",
    'ak.noAchievementsYet': "لا توجد إنجازات تدريب بعد",
    'ak.noAchievementsDesc': "ستظهر المعالم هنا بمجرد تفعيل تدريب النموذج.",
    'ak.exportReportUnavailableDesc': "سيكون تصدير تقرير الأداء متاحًا في إصدار قادم.",
    'ak.exportPerformanceReport': "تصدير تقرير الأداء",
    'ak.failedLoadKb': "فشل تحميل مدخلات قاعدة المعرفة",
    'ak.fillTitleContent': "يرجى ملء العنوان والمحتوى",
    'ak.entryCreated': "تم إنشاء المدخل",
    'ak.entryCreatedDesc': "تم إنشاء مدخل قاعدة المعرفة بنجاح",
    'ak.failedCreateEntry': "فشل إنشاء مدخل قاعدة المعرفة",
    'ak.entryUpdated': "تم تحديث المدخل",
    'ak.entryUpdatedDesc': "تم تحديث مدخل قاعدة المعرفة بنجاح",
    'ak.failedUpdateEntry': "فشل تحديث مدخل قاعدة المعرفة",
    'ak.entryDeleted': "تم حذف المدخل",
    'ak.entryDeletedDesc': "تم حذف مدخل قاعدة المعرفة بنجاح",
    'ak.failedDeleteEntry': "فشل حذف مدخل قاعدة المعرفة",
    'ak.nothingToExport': "لا يوجد شيء للتصدير",
    'ak.nothingToExportDesc': "لا توجد مدخلات في قاعدة المعرفة للتصدير بعد.",
    'ak.kbExportedDesc': "تم تصدير قاعدة المعرفة بتنسيق JSON.",
    'ak.importUnavailableDesc': "سيكون الاستيراد متاحًا في إصدار قادم.",
    'ak.loadingKb': "جارٍ تحميل قاعدة المعرفة...",
    'ak.kbManagementTitle': "إدارة قاعدة المعرفة",
    'ak.kbManagementSubtitle': "إدارة مدخلات معرفة الذكاء الاصطناعي ومحتوى التدريب",
    'ak.entriesBadge': "{count} مدخلات",
    'ak.import': "استيراد",
    'ak.export': "تصدير",
    'ak.totalEntries': "إجمالي المدخلات",
    'ak.avgSuccessRate': "متوسط معدل النجاح",
    'ak.totalUsage': "إجمالي الاستخدام",
    'ak.categories': "الفئات",
    'ak.tab.browseEntries': "تصفح المدخلات",
    'ak.tab.createEntry': "إنشاء مدخل",
    'ak.tab.analytics': "التحليلات",
    'ak.searchKbPlaceholder': "البحث في قاعدة المعرفة...",
    'ak.allCategories': "جميع الفئات",
    'ak.usedTimesUpdated': "استُخدم {count} مرة · آخر تحديث {date}",
    'ak.resolutionStepsLabel': "خطوات الحل:",
    'ak.noEntriesFound': "لم يتم العثور على مدخلات",
    'ak.noEntriesDesc': "حاول تعديل بحثك أو أنشئ مدخلاً جديدًا في قاعدة المعرفة",
    'ak.createKbEntry': "إنشاء مدخل في قاعدة المعرفة",
    'ak.entryTitleRequired': "عنوان المدخل *",
    'ak.enterEntryTitle': "أدخل عنوان المدخل",
    'ak.contentRequired': "المحتوى *",
    'ak.enterKbContent': "أدخل محتوى قاعدة المعرفة",
    'ak.tagsCommaSeparated': "الوسوم (مفصولة بفواصل)",
    'ak.tagsExamplePlaceholder': "كلمة المرور، إعادة تعيين، تسجيل الدخول، الأمان",
    'ak.resolutionStepsOnePerLine': "خطوات الحل (خطوة واحدة في كل سطر)",
    'ak.resolutionStepsPlaceholder': "الخطوة 1: انتقل إلى صفحة تسجيل الدخول\nالخطوة 2: انقر على نسيت كلمة المرور\nالخطوة 3: أدخل عنوان البريد الإلكتروني",
    'ak.saveEntry': "حفظ المدخل",
    'ak.kbAnalytics': "تحليلات قاعدة المعرفة",
    'ak.performanceOverview': "نظرة عامة على الأداء",
    'ak.overallSuccessRate': "معدل النجاح الإجمالي",
    'ak.knowledgeCoverage': "تغطية المعرفة",
    'ak.usageFrequency': "تكرار الاستخدام",
    'ak.topPerformingEntries': "المدخلات الأعلى أداءً",
    'ak.viewDetailedAnalytics': "عرض التحليلات التفصيلية",
    'ak.editKbEntry': "تعديل مدخل قاعدة المعرفة",
    'ak.editKbEntryDesc': "عدّل تفاصيل مدخل قاعدة المعرفة",
    'ak.updateEntry': "تحديث المدخل",
    'ak.areYouSure': "هل أنت متأكد؟",
    'ak.deleteConfirmPrefix': "لا يمكن التراجع عن هذا الإجراء. سيؤدي هذا إلى حذف مدخل قاعدة المعرفة نهائيًا",
    'ak.deleteConfirmSuffix': "وإزالة جميع البيانات المرتبطة به.",
    'ak.deleteEntry': "حذف المدخل",
    'ak.detailedKbAnalytics': "تحليلات تفصيلية لقاعدة المعرفة",
    'ak.detailedKbAnalyticsDesc': "تحليل شامل لأداء قاعدة المعرفة والرؤى",
    'ak.categoryPerformanceAnalysis': "تحليل الأداء حسب الفئة",
    'ak.entriesLower': "{count} مدخلات",
    'ak.successRate': "معدل النجاح",
    'ak.usage': "الاستخدام",
    'ak.aiPerformanceInsights': "رؤى أداء الذكاء الاصطناعي",
    'ak.highPerformingContent': "المحتوى عالي الأداء",
    'ak.entriesAbove90': "{count} مدخلات تتجاوز معدلات نجاحها 90%",
    'ak.optimizationOpportunities': "فرص التحسين",
    'ak.entriesBenefitOptimization': "{count} مدخلات قد تستفيد من التحسين",
    'ak.usageTrendsRecommendations': "اتجاهات الاستخدام والتوصيات",
    'ak.mostUsedEntry': "المدخل الأكثر استخدامًا:",
    'ak.uses': "استخدامات",
    'ak.bestPerformingEntry': "المدخل الأعلى أداءً:",
    'ak.success': "نجاح",
    'ak.aiRecommendations': "توصيات الذكاء الاصطناعي:",
    'ak.recExpandCategories': "فكّر في توسيع الفئات عالية الأداء بمزيد من المدخلات",
    'ak.recReviewLowSuccess': "راجع وحسّن المدخلات ذات معدلات النجاح الأقل من 80%",
    'ak.recAddResolutionSteps': "أضف مزيدًا من خطوات الحل إلى المدخلات المستخدمة بكثرة",
    'ak.recMonitorFeedback': "راقب تعليقات العملاء لتحسين المحتوى",
    'ak.failedLoadAnalytics': "فشل تحميل بيانات تحليلات الذكاء الاصطناعي",
    'ak.analyticsExportedDesc': "تم تصدير بيانات تحليلات الذكاء الاصطناعي بنجاح",
    'ak.loadingAnalytics': "جارٍ تحميل تحليلات الذكاء الاصطناعي...",
    'ak.analyticsDashboardTitle': "لوحة تحليلات الذكاء الاصطناعي",
    'ak.analyticsDashboardSubtitle': "مقاييس ورؤى شاملة لأداء الذكاء الاصطناعي",
    'ak.range.24h': "آخر 24 ساعة",
    'ak.range.7d': "آخر 7 أيام",
    'ak.range.30d': "آخر 30 يومًا",
    'ak.range.90d': "آخر 90 يومًا",
    'ak.lastUpdated': "آخر تحديث: {time}",
    'ak.totalConversations': "إجمالي المحادثات",
    'ak.aiHandled': "تمت معالجتها بالذكاء الاصطناعي",
    'ak.autoResolutionRate': "معدل الحل التلقائي",
    'ak.currentPeriod': "الفترة الحالية",
    'ak.avgResponseTime': "متوسط وقت الاستجابة",
    'ak.customerSatisfaction': "رضا العملاء",
    'ak.tab.categoryAnalysis': "تحليل الفئات",
    'ak.tab.trendsPatterns': "الاتجاهات والأنماط",
    'ak.tab.aiInsights': "رؤى الذكاء الاصطناعي",
    'ak.resolutionPerformance': "أداء الحل",
    'ak.successfulResolutions': "الحلول الناجحة",
    'ak.escalationRate': "معدل التصعيد",
    'ak.aiConfidenceScore': "درجة ثقة الذكاء الاصطناعي",
    'ak.hourlyPerformance': "الأداء بالساعة",
    'ak.conversationsCount': "{count} محادثات",
    'ak.performanceByCategory': "الأداء حسب الفئة",
    'ak.performanceTrends': "اتجاهات الأداء",
    'ak.trendComparisonsUnavailable': "مقارنات الاتجاهات غير متاحة بعد",
    'ak.trendComparisonsDesc': "ستكون اتجاهات الفترة مقابل الفترة متاحة في إصدار قادم.",
    'ak.usagePatterns': "أنماط الاستخدام",
    'ak.peakHours': "ساعات الذروة",
    'ak.mostCommonIssues': "المشكلات الأكثر شيوعًا",
    'ak.resolutionTime': "وقت الحل",
    'ak.average': "المتوسط",
    'ak.aiInsightsUnavailable': "رؤى أداء الذكاء الاصطناعي غير متاحة بعد",
    'ak.aiInsightsDesc': "سيكون التحليل التلقائي للرؤى متاحًا في إصدار قادم.",
    'ak.bestPracticeGuidance': "إرشادات أفضل الممارسات",
    'ak.bestPracticeNote': "إرشادات عامة لتحسين أداء الذكاء الاصطناعي — غير مستمدة من بياناتك المباشرة.",
    'ak.immediateActions': "إجراءات فورية",
    'ak.immediate1': "إضافة مزيد من مدخلات الدعم التقني إلى قاعدة المعرفة",
    'ak.immediate2': "مراجعة المحادثات المصعّدة لتحليل الأنماط",
    'ak.immediate3': "تحديث بيانات التدريب لسيناريوهات الفوترة المعقدة",
    'ak.strategicImprovements': "تحسينات استراتيجية",
    'ak.strategic1': "تنفيذ تحليل المشاعر لتوجيه أفضل",
    'ak.strategic2': "إضافة دعم متعدد اللغات للعملاء حول العالم",
    'ak.strategic3': "تطوير قدرات الكشف الاستباقي عن المشكلات",
    'ak.teamDevelopment': "تطوير الفريق",
    'ak.team1': "تدريب الوكلاء على الحالات المصعّدة بالذكاء الاصطناعي",
    'ak.team2': "إنشاء حلقات تغذية راجعة للتحسين المستمر",
    'ak.team3': "تطوير سير عمل التعاون بين الذكاء الاصطناعي والبشر",
  },
};

export default aiKnowledgeTranslations;
